'use client';
import { AliquotsDuPoint } from './AliquotsDuPoint';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { enTeteCollant, Tableau } from '@/shared/Tableau';
import { fetchEcriture } from '@/lib/ecrire';
import { useGed, userName } from '@/lib/store';
import { peut, peutOuRepli, estBiologiste, fmtDate, CAL_TYPES, CAL_CAUSES, isoJour, aujourdhuiIso } from '@/lib/meta';
import { changementsDeLot } from '@/lib/changements-lot';
import { I, Modale, card, inputStyle, useBusy, RefISO, Selecteur, Skeleton, enChargement, useConfirmation } from '@/components/ui';
import { BoutonJournal } from '@/components/BoutonJournal';
import { useAutorisationResponsable, ChampsResponsable } from '@/components/AutorisationResponsable';
import { H1 } from './commun';
import { PropositionsNc } from './PropositionsNc';
import { Norme } from '../../Norme';

import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
import { Frise } from '@/shared/Frise';
/* ─────────────────── CQI — cartes de contrôle (lot C3) ───────────────────
 * Remplace l'écran bâti sur l'ancien modèle plat (`ged-cqi`), qui ne portait
 * qu'une cible par contrôle et ignorait les périodes de lot.
 *
 * Deux modes de lecture, parce que le laboratoire a besoin des deux :
 *  · PAR LOT — le graphe se réinitialise à chaque changement de lot de contrôle.
 *    C'est la lecture métier : un décrochage au changement de lot doit sauter
 *    aux yeux. Mode par défaut.
 *  · PAR PÉRIODE — plusieurs lots coexistent. L'axe reste en σ, chaque point
 *    normalisé sur la cible de SON lot : c'est ce qui les rend comparables
 *    malgré des cibles différentes. Les frontières de lot sont matérialisées.
 * ------------------------------------------------------------------------- */

type Cible = { niveau: number; lot: string | null; cible: number; sd: number; qualitatif?: boolean; attendu?: 'inferieur' | 'superieur'; limite?: number };
/** La cible qui FAIT FOI, avec sa provenance. Un niveau peut être retombé en
 *  repli fournisseur alors que la carte est déclarée en cible interne : c'est
 *  `source` qui dit la vérité, pas l'intitulé du bouton. */
type CibleEff = Cible & { source: 'fournisseur' | 'interne'; n?: number; repli?: string; approuveeLe?: string };
/** Une cible interne FIGÉE à l'approbation (R1, 07/10/2026). */
type Figee = { niveau: number; cible: number; sd: number; cv: number; n: number };
/** R3 — le lot de contrôle est-il valable au jour de la lecture ? Nommé. */
type LotUtilisable = { utilisable: boolean; motif?: string; nature?: string; avertissement?: string };
type Stat = { niveau: number; n: number; moyenne: number; sd: number; cv: number; biais: number | null };
type Declenchement = { code: string; gravite: 'alerte' | 'rejet'; niveau: number | null; libelle?: string };
type Couleur = 'vert' | 'jaune' | 'rouge';
type Conduite = {
  cause: string | null; action: string | null; responsable: string | null; le: string | null;
  decision: 'a_repeter' | 'accepte_avec_derogation' | null; justification: string | null; decisionLe: string | null;
  remplacementPassage: string | null; clotureCommentaire: string | null; clotureLe: string | null;
};
export type Point = {
  passageId: string; date: string; numeroSerie: number | null; operateur: string | null;
  /** Le lot de RÉACTIF de ce passage — servi par la route depuis le
   *  12/09/2026. ⚠️ À ne pas confondre avec `Bloc.lotControle` : ce sont deux
   *  registres. Le lot de CONTRÔLE ouvre une carte neuve (cible et écart-type
   *  changent) ; le lot de RÉACTIF se marque SUR la carte en cours. */
  lotReactif: string | null;
  valeurs: { niveau: number; valeur: number | null; z: number | null; rejete?: boolean; rejeteMotif?: string | null; texte?: string | null; qualitatif?: boolean; conforme?: boolean | null }[];
  commentaire: string | null; validation: string; motifDerogation: string | null; importe: boolean;
  /** `baseline` (R1) : point de constitution d'une cible interne non approuvée — aucun verdict. */
  regles: Declenchement[]; statut: 'conforme' | 'alerte' | 'rejet' | 'baseline';
  /* R2 — le verdict d'ORIGINE (figé à l'enregistrement) et les réévaluations tracées.
     `verdictRevise` : une origine existe ET diffère du verdict courant. */
  statutOrigine?: 'conforme' | 'alerte' | 'rejet' | 'baseline' | null;
  reglesOrigine?: Declenchement[] | null;
  cibleOrigine?: { niveau: number; cible: number; sd: number; source: string }[] | null;
  verdictFigeLe?: string | null;
  reevaluations?: { le: string; parKey: string; parNom: string; motif: string; statutAvant: string; statutApres: string; changement: string }[];
  verdictRevise?: boolean;
  /* R6 — la conduite tenue après un rejet ; `aTraiter` = rejet sans cause + action + responsable. */
  conduite?: Conduite;
  aTraiter?: boolean;
  repasse: boolean; couleur: Couleur; enAttente: boolean;
  suite: 'derogation' | 'repasse' | 'calibration' | 'valide' | null; exige: string | null;
};
type Recevabilite = {
  recevable: boolean; motifs: string[];
  details: { niveau: number; n: number; sdLabo: number; sdFournisseur: number; biaisSigma: number | null }[];
};
export type Bloc = {
  periodeId: string; lotControle: string | null; datePeremption: string | null;
  cloturee: boolean; sourceCible: string; cibles: Cible[]; ciblesEffectives: CibleEff[];
  statsInternes: Stat[]; recevabiliteInterne: Recevabilite; points: Point[];
  /* R1 — l'état de la cible interne : en constitution (`baseline`) ou FIGÉE,
     avec la date, l'approbateur et les constats de recevabilité à l'acte. */
  baseline?: boolean; pointsRequis?: number;
  ciblesInternesFigees?: Figee[]; approuveeLe?: string | null; approuveeParNom?: string | null;
  approuveeDetails?: Recevabilite['details'] | null;
  /* R3 — le lot est-il encore utilisable au jour de la lecture ? */
  lotUtilisable?: LotUtilisable | null;
  /* Refonte CQI L1 (27/10/2026) — les informations du lot, complétables sans
     ouvrir de nouvelle période (`POST /ged/cqi-periodes/:id/infos`). */
  marqueControle?: string | null; refControle?: string | null; insertUrl?: string | null;
  lotsNiveaux?: { niveau: string; numeroLot: string; codeNiveau?: string; flaconRef?: string }[] | null;
};
export type Resume = {
  carteId: string; analyte: string; analyteNom: string; matiere: string | null;
  equipement: string; equipementNom: string; equipementId: string | null;
  discipline: string; unite: string | null;
  nbNiveaux: number; reglesActives: string[]; minPointsInterne: number;
  statut: string; derniereRegle: string | null; enAttente: number;
  dernierPassage: string | null; points: number; periodes: number;
  sourceCible: string | null; sourceDemandee: string | null; aParametrer: boolean;
  /** R1 — cible interne demandée, pas approuvée ; `pointsBaseline` = le niveau le moins fourni. */
  baseline?: boolean; pointsBaseline?: number;
  /** R3 — péremption du lot ouvert et son verdict du jour. */
  datePeremption?: string | null; lotUtilisable?: LotUtilisable | null;
  /* CQI-3 / CQI-4 — le réactif dosé, son fournisseur, et les deux lots.
     `lotReactif` (coffret) et `lotControle` (sérum de contrôle) sont DEUX
     choses : relevé en base le 07/08/2026, les deux colonnes n'ont aucune
     valeur commune et un lot de contrôle couvre plusieurs analytes quand un
     lot de réactif n'en couvre qu'un. */
  fournisseur: string | null; fournisseurId: string | null;
  reactifDesignation: string | null; reactifRef: string | null;
  lotReactif: string | null; lotControle: string | null;
};
type Recap = { total: number; rejet: number; alerte: number; conforme: number; sansCible: number; enAttente: number; baseline?: number; lotsInutilisables?: number };

/** R2 — ce que le serveur rend AVANT d'écrire un changement qui rejuge la série. */
type ImpactApercu = {
  pointsConcernes: number; changentDeVerdict: number; changentDeRegles: number;
  detail: { passageId: string; datePassage: string; numeroSerie: number | null; statutAvant: string; statutApres: string; reglesApparues: string[]; reglesDisparues: string[] }[];
};
/** Le résumé lisible d'un aperçu, pour la question posée avant d'écrire. */
function phraseImpact(a: ImpactApercu): string {
  const ex = a.detail.filter((d) => d.statutAvant !== d.statutApres).slice(0, 6)
    .map((d) => `${fmtDate(d.datePassage)}${d.numeroSerie ? ` n°${d.numeroSerie}` : ''} : ${d.statutAvant} → ${d.statutApres}`).join(' · ');
  return `${a.changentDeVerdict} point(s) déjà saisi(s) sur ${a.pointsConcernes} changeraient de verdict`
    + (a.changentDeRegles ? ` (et ${a.changentDeRegles} de règles sans changer de verdict)` : '')
    + `. ${ex}${a.changentDeVerdict > 6 ? ' · …' : ''}`;
}
/**
 * R2 — écrire un changement qui peut rejuger des points : APERÇU d'abord, puis
 * motif obligatoire si des verdicts changent (annulable), puis écriture. Le
 * serveur refuse de toute façon sans motif ; ici on pose la question avant.
 * Rend `null` si l'utilisateur a renoncé.
 */
async function ecrireAvecApercu(
  url: string, corps: Record<string, unknown>,
  demanderTexte: (q: string, o?: { titre?: string; confirmer?: string; placeholder?: string; min?: number; multiligne?: boolean }) => Promise<string | null>,
): Promise<Response | null> {
  const envoyer = (b: Record<string, unknown>) => fetchEcriture(url, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(b),
  });
  const ra = await envoyer({ ...corps, apercu: true });
  const ja = ra.ok ? await ra.json().catch(() => ({})) : {};
  const apercu: ImpactApercu | undefined = ja.apercu;
  if (apercu && apercu.changentDeVerdict > 0) {
    const motif = await demanderTexte(
      `${phraseImpact(apercu)}\n\nPourquoi ce changement ? Le motif sera écrit sur chacun de ces points, avec le verdict qu'ils avaient avant.`,
      { titre: 'Des points déjà saisis changent de verdict', confirmer: 'Appliquer et tracer', placeholder: 'Motif (5 caractères au moins)', min: 5, multiligne: true },
    );
    if (motif === null) return null;
    return envoyer({ ...corps, motif });
  }
  return envoyer(corps);
}
type Detail = {
  // Politique de rejet EN VIGUEUR, servie par le serveur — l'écran n'a pas de
  // second appel de réglages à faire pour savoir s'il doit barrer un point
  // rejeté ou l'écarter complètement du tracé.
  rejetAffichage: boolean;
  rejetCalcul: boolean;
  carte: {
    carteId: string; analyte: string; analyteNom: string; equipement: string; equipementNom: string;
    equipementId: string | null; discipline: string; unite: string | null; nbNiveaux: number;
    libellesNiveaux: string[]; actif: boolean; reglesActives: string[]; minPointsInterne: number; statut: string;
    analyteId?: string | null;
    techniques?: { code: string; nom: string }[];
    /** La technique que CETTE carte contrôle (18/08) — distincte de
     *  `techniques`, qui liste toutes celles du paramètre. */
    technique?: { code: string; nom: string; principe: string | null } | null;
    techniqueId?: string | null;
    examens?: { code: string; libelle: string }[];
    fournisseur: string | null; fournisseurId: string | null;
    reactifDesignation: string | null; reactifRef: string | null;
  };
  catalogue: { code: string; libelle: string; description: string; gravite: string }[];
  /** Toutes les périodes de la carte — ouvertes ET clôturées. Avec leurs dates
   *  depuis le 12/09/2026 : pour retrouver une carte archivée, on cherche une
   *  période, pas un identifiant de lot. */
  periodes: {
    periodeId: string; lotControle: string | null; cloturee: boolean;
    dateDebut: string | null; dateFin: string | null; datePeremption: string | null;
    points: number | null;
  }[];
  blocs: Bloc[];
};

const COULEURS = ['var(--cyan-600)', 'var(--violet-600)', 'var(--amber-600)', 'var(--green-600)'];
export const TONS: Record<string, { bg: string; fg: string; label: string }> = {
  conforme: { bg: 'var(--green-50)', fg: 'var(--green-600)', label: 'Conforme' },
  alerte: { bg: 'var(--amber-50)', fg: 'var(--amber-600)', label: 'Avertissement' },
  rejet: { bg: 'var(--red-50)', fg: 'var(--red-600)', label: 'Rejet' },
  // R1 — ni conforme ni rejet : la cible interne se constitue, aucune règle n'est évaluée.
  baseline: { bg: 'var(--cyan-50)', fg: 'var(--cyan-700)', label: 'Baseline' },
};

/* ─────────────── R6 — CONDUITE À TENIR APRÈS UN REJET ───────────────
 *
 * Frise en QUATRE ÉTAPES qui ne sont pas des onglets (même forme que « Action :
 * avancement & suivi ») : contexte → action corrective → décision → clôture.
 * Le contexte est le panneau du point lui-même (valeur, cible, σ, z, règles).
 * Un rejet reste « à traiter » tant que cause + action + responsable manquent.
 * ⚠️ A2 : rien ici n'ouvre de NC — la proposition existante s'affiche à côté,
 * l'utilisateur décide. */
function ConduiteRejet({ point, carteId, peutSaisir, peutValider, peutDeroger, personnes, onFait, onNouveauPassage }: {
  point: Point; carteId: string; peutSaisir: boolean; peutValider: boolean; peutDeroger: boolean;
  personnes: { id: string; nom: string }[]; onFait: (message: string) => void | Promise<void>; onNouveauPassage: () => void;
}) {
  const c = point.conduite || { cause: null, action: null, responsable: null, le: null, decision: null, justification: null, decisionLe: null, remplacementPassage: null, clotureCommentaire: null, clotureLe: null };
  const clos = !!c.clotureLe;
  const instruite = !!(c.cause && c.action && c.responsable);
  const etape: 'contexte' | 'action' | 'decision' | 'cloture' = clos ? 'cloture' : c.decision ? 'decision' : instruite ? 'action' : 'contexte';
  const [cause, setCause] = useState(c.cause || '');
  const [action, setAction] = useState(c.action || '');
  const [responsable, setResponsable] = useState(c.responsable || '');
  const [decision, setDecision] = useState<'a_repeter' | 'accepte_avec_derogation' | ''>(c.decision || '');
  const [justification, setJustification] = useState(c.justification || '');
  const [remplacement, setRemplacement] = useState(c.remplacementPassage || '');
  const [candidats, setCandidats] = useState<{ passageId: string; date: string; numeroSerie: number | null; statut: string }[] | null>(null);
  const [commentaire, setCommentaire] = useState(c.clotureCommentaire || '');
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();
  /* Un autre point, ou la conduite relue après une écriture : la saisie
     repart de ce qui est enregistré. Recalé PENDANT LE RENDU (motif de
     `ui.tsx`), pas dans un effet. */
  const cleConduite = JSON.stringify([point.passageId, c.cause, c.action, c.responsable, c.decision, c.justification, c.remplacementPassage, c.clotureCommentaire]);
  const [conduiteVue, setConduiteVue] = useState(cleConduite);
  if (conduiteVue !== cleConduite) {
    setConduiteVue(cleConduite);
    setCause(c.cause || ''); setAction(c.action || ''); setResponsable(c.responsable || '');
    setDecision(c.decision || ''); setJustification(c.justification || ''); setRemplacement(c.remplacementPassage || ''); setCommentaire(c.clotureCommentaire || '');
    setErreur(''); setCandidats(null);
  }
  useEffect(() => {
    if (decision !== 'a_repeter' || clos) return;
    let vivant = true;
    void fetch(`/api/ged/ged/cqi-passages/${point.passageId}/remplacements`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { remplacements: [] }))
      .then((j) => { if (vivant) setCandidats(j.remplacements || []); })
      .catch(() => { if (vivant) setCandidats([]); });
    return () => { vivant = false; };
  }, [decision, point.passageId, clos]);
  const poster = (chemin: string, corps: Record<string, unknown>, message: string) => run(chemin, async () => {
    setErreur('');
    const r = await fetchEcriture(`/api/ged/ged/cqi-passages/${point.passageId}/${chemin}`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(corps),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { setErreur(j.error?.message || 'Refusé.'); return; }
    await onFait(message);
  });
  const nomDe = (k: string | null) => personnes.find((p) => p.id === k)?.nom || k || '—';
  const ETAPES = [
    { k: 'contexte', label: 'Contexte' }, { k: 'action', label: 'Action corrective' }, { k: 'decision', label: 'Décision' }, { k: 'cloture', label: 'Clôture' },
  ] as const;
  const champ = { ...inputStyle, fontSize: 12.5 };
  return (
    <div style={{ display: 'grid', gap: 8, padding: '10px 12px', borderRadius: 'var(--radius-md)', border: `1px solid ${point.aTraiter ? 'var(--red-200)' : 'var(--border-default)'}`, background: point.aTraiter ? 'var(--red-50)' : 'var(--surface-card)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 12.5 }}>Conduite tenue après le rejet</strong>
        {point.aTraiter && <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 8px', background: 'var(--red-50)', color: 'var(--red-600)', border: '1px solid var(--red-200)' }}>à traiter</span>}
        {clos && <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 8px', background: 'var(--green-50)', color: 'var(--green-600)' }}>clos le {fmtDate(c.clotureLe!)} — lecture seule</span>}
      </div>
      {/* La frise : des étapes, pas des onglets — le composant commun (lot D). */}
      <Frise etapes={[...ETAPES]} courante={etape} achevee={clos} label="Avancement de la conduite tenue" />
      {erreur && <div style={{ fontSize: 12, color: 'var(--red-600)' }}>{erreur}</div>}

      {/* 1 — contexte : le panneau au-dessus, et deux raccourcis. */}
      {!clos && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <Bouton taille="sm" onClick={onNouveauPassage} title="Ouvrir la saisie de la série pour repasser ce contrôle (contexte : même automate)">Saisir un nouveau passage</Bouton>
        </div>
      )}

      {/* 2 — action corrective */}
      <div style={{ display: 'grid', gap: 6 }}>
        <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>Action corrective</span>
        {clos ? (
          <span style={{ fontSize: 12.5 }}>Cause : {c.cause} · Action : {c.action} · Responsable : {nomDe(c.responsable)}{c.le ? ` · le ${fmtDate(c.le)}` : ''}</span>
        ) : (<>
          <input value={cause} onChange={(e) => setCause(e.target.value)} placeholder="Cause constatée (obligatoire)" aria-label="Cause" style={champ} disabled={!peutSaisir} />
          <input value={action} onChange={(e) => setAction(e.target.value)} placeholder="Action corrective faite (obligatoire)" aria-label="Action corrective" style={champ} disabled={!peutSaisir} />
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <select value={responsable} onChange={(e) => setResponsable(e.target.value)} aria-label="Responsable" style={{ ...champ, width: 220 }} disabled={!peutSaisir}>
              <option value="">— responsable —</option>
              {personnes.map((p) => <option key={p.id} value={p.id}>{p.nom}</option>)}
            </select>
            <Bouton taille="sm" variante="primaire" disabled={!peutSaisir || estOccupe() || cause.trim().length < 5 || action.trim().length < 5 || !responsable}
              title={!peutSaisir ? 'Droit « cqiSaisir » requis' : cause.trim().length < 5 ? 'La cause est obligatoire (5 caractères)' : action.trim().length < 5 ? 'L’action est obligatoire (5 caractères)' : !responsable ? 'Choisir le responsable' : 'Enregistre cause, action et responsable (journalisé)'}
              onClick={() => void poster('conduite', { cause, action, responsable }, 'Conduite tenue enregistrée.')}>
              {instruite ? 'Mettre à jour' : 'Enregistrer'}
            </Bouton>
          </div>
        </>)}
      </div>

      {/* 3 — décision */}
      {(instruite || clos) && (
        <div style={{ display: 'grid', gap: 6 }}>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>Décision</span>
          {clos ? (
            <span style={{ fontSize: 12.5 }}>
              {c.decision === 'a_repeter' ? `À répéter — remplacement ${c.remplacementPassage ? 'désigné' : 'non désigné'}` : `Accepté avec dérogation — ${c.justification}`}{c.decisionLe ? ` · le ${fmtDate(c.decisionLe)}` : ''}
            </span>
          ) : (<>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12.5 }}>
              <input type="radio" checked={decision === 'a_repeter'} onChange={() => setDecision('a_repeter')} disabled={!peutValider} />À répéter — une mesure de remplacement est obligatoire avant clôture
            </label>
            {decision === 'a_repeter' && (
              <select value={remplacement} onChange={(e) => setRemplacement(e.target.value)} aria-label="Mesure de remplacement" style={{ ...champ, width: 320 }} disabled={!peutValider}>
                <option value="">{candidats === null ? 'Recherche des passages de remplacement…' : candidats.length ? '— choisir la mesure de remplacement —' : 'Aucun passage postérieur conforme ou en alerte sur ce lot : saisir un nouveau passage d’abord'}</option>
                {(candidats || []).map((k) => <option key={k.passageId} value={k.passageId}>{fmtDate(k.date)}{k.numeroSerie ? ` n°${k.numeroSerie}` : ''} — {k.statut}</option>)}
              </select>
            )}
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12.5 }}>
              <input type="radio" checked={decision === 'accepte_avec_derogation'} onChange={() => setDecision('accepte_avec_derogation')} disabled={!peutValider || !peutDeroger} title={!peutDeroger ? 'Droit « cqiDeroger » requis' : undefined} />
              Accepté avec dérogation{!peutDeroger ? ' (droit « cqiDeroger » requis)' : ''}
            </label>
            {decision === 'accepte_avec_derogation' && (
              <input value={justification} onChange={(e) => setJustification(e.target.value)} placeholder="Justification de la dérogation (obligatoire)" aria-label="Justification" style={champ} disabled={!peutValider} />
            )}
            <div>
              <Bouton taille="sm" variante="primaire" disabled={!peutValider || estOccupe() || !decision || (decision === 'accepte_avec_derogation' && justification.trim().length < 5)}
                title={!peutValider ? 'Droit « cqiValider » requis' : !decision ? 'Choisir une décision' : decision === 'accepte_avec_derogation' && justification.trim().length < 5 ? 'La justification est obligatoire (5 caractères)' : 'Enregistre la décision (journalisé)'}
                onClick={() => void poster('conduite/decision', decision === 'a_repeter' ? { decision, remplacementPassage: remplacement || undefined } : { decision, justification }, 'Décision enregistrée.')}>
                Enregistrer la décision
              </Bouton>
            </div>
          </>)}
        </div>
      )}

      {/* 4 — clôture */}
      {(c.decision || clos) && (
        <div style={{ display: 'grid', gap: 6 }}>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>Clôture</span>
          {clos ? (
            <span style={{ fontSize: 12.5 }}>{c.clotureCommentaire}</span>
          ) : (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <input value={commentaire} onChange={(e) => setCommentaire(e.target.value)} placeholder="Commentaire de clôture (obligatoire)" aria-label="Commentaire de clôture" style={{ ...champ, flex: 1, minWidth: 220 }} disabled={!peutValider} />
              <Bouton taille="sm" variante="primaire" disabled={!peutValider || estOccupe() || commentaire.trim().length < 5 || (c.decision === 'a_repeter' && !c.remplacementPassage)}
                title={!peutValider ? 'Droit « cqiValider » requis' : c.decision === 'a_repeter' && !c.remplacementPassage ? 'Désigner d’abord la mesure de remplacement' : commentaire.trim().length < 5 ? 'Le commentaire est obligatoire (5 caractères)' : 'Clôt la conduite : ensuite lecture seule (journalisé)'}
                onClick={() => void poster('conduite/cloturer', { commentaire }, 'Conduite close — lecture seule.')}>
                Clore
              </Bouton>
            </div>
          )}
        </div>
      )}
      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Aucune non-conformité n’est ouverte d’office : la proposition, s’il y en a une, se décide à part, cas par cas.</span>
    </div>
  );
}

/* ─────────────── R1 — CONSULTER OU APPROUVER LA CIBLE INTERNE ───────────────
 *
 * Ce que l'approbateur a sous les yeux : la statistique COURANTE du lot (n,
 * moyenne, σ, CV, biais) face au certificat, les constats de recevabilité, et
 * les motifs de refus s'il y en a. Le bouton n'approuve que si c'est recevable
 * ET si la personne en a le droit — sinon il DIT ce qui manque (R10). Le
 * serveur refait le même calcul et refuse de la même façon. */
function ApprobationCibleInterne({
  bloc, libelles, unite, pointsRequis, peutApprouver, occupe, onClose, onApprouver,
}: {
  bloc: Bloc; libelles: string[]; unite: string | null; pointsRequis: number;
  peutApprouver: boolean; occupe: boolean; onClose: () => void;
  onApprouver: (commentaire: string) => void | Promise<unknown>;
}) {
  const [commentaire, setCommentaire] = useState('');
  const nom = (n: number) => libelles[n - 1]?.trim() || `Niveau ${n}`;
  const rec = bloc.recevabiliteInterne;
  const figee = !bloc.baseline && bloc.sourceCible === 'interne' && (bloc.ciblesInternesFigees || []).length > 0;
  const empeche = figee ? 'Déjà approuvée — une nouvelle cible se constitue sur un nouveau lot'
    : !bloc.baseline ? 'Demander d’abord la cible interne (bouton « Demander une cible interne »)'
      : !rec?.recevable ? `Non recevable : ${rec?.motifs.join(' · ')}`
        : !peutApprouver ? 'Approuver exige le droit « cqiApprouverCible » (ou « cqiValider » à défaut de paramétrage)'
          : '';
  const td = { padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', textAlign: 'right' as const, fontFamily: 'var(--font-mono)', fontSize: 'var(--tableau-ligne-police)' };
  return (
    <Modale titre={figee ? 'Cible interne figée' : 'Consulter ou approuver la cible interne'}
      sousTitre={`Lot ${bloc.lotControle || '?'} · ${bloc.points.length} passage(s) · ${pointsRequis} points requis par niveau`}
      onClose={onClose} largeur={720}
      pied={(
        <>
          <Bouton onClick={onClose}>Fermer</Bouton>
          {!figee && (
            <Bouton variante="primaire" disabled={!!empeche || occupe || (bloc.points.length > 0 && commentaire.trim().length < 5)}
              title={empeche || (bloc.points.length > 0 && commentaire.trim().length < 5 ? 'Un motif de 5 caractères au moins est requis : il sera écrit sur chaque point de baseline rejugé' : 'Fige la moyenne et l’écart-type observés comme cible du lot : les limites ne bougeront plus')}
              onClick={() => void onApprouver(commentaire)}>
              Approuver et figer la cible
            </Bouton>
          )}
        </>
      )}>
      <div style={{ display: 'grid', gap: 12 }}>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
          {figee
            ? `Approuvée le ${bloc.approuveeLe ? fmtDate(bloc.approuveeLe) : '?'}${bloc.approuveeParNom ? ` par ${bloc.approuveeParNom}` : ''}. Ce sont ces valeurs qui portent les limites de la carte ; la statistique courante, à droite, ne les déplace pas.`
            : 'Approuver FIGE la moyenne et l’écart-type observés comme cible du lot. À partir de là, saisir un passage ne déplace plus aucune limite — c’est ce qui rend une dérive lente visible. Re-figer n’existe pas : une nouvelle cible = un nouveau lot.'}
        </p>
        {/* L10 (11/10/2026) : synthèse par niveau sur le socle, niveau SIMPLE — les
            nombres à droite, chasse fixe ; les lectures (stats internes, détails,
            figée) se font par niveau dans chaque colonne. */}
        <Tableau
          id="cqi-cibles-niveaux"
          niveau="simple"
          lignes={bloc.cibles.filter((c) => !c.qualitatif)}
          cleLigne={(c) => String(c.niveau)}
          colonnes={[
            { cle: 'niveau', libelle: 'Niveau', valeur: (c) => c.niveau, style: { fontWeight: 700 }, rendu: (c) => `N${c.niveau}` },
            { cle: 'cible', libelle: 'Cible fourn.', type: 'nombre', valeur: (c) => c.cible, rendu: (c) => `${c.cible}${unite ? ` ${unite}` : ''}` },
            { cle: 'sd', libelle: 'σ fourn.', type: 'nombre', valeur: (c) => c.sd, rendu: (c) => c.sd },
            {
              cle: 'n', libelle: 'n', type: 'nombre', valeur: (c) => bloc.statsInternes.find((x) => x.niveau === c.niveau)?.n ?? 0,
              rendu: (c) => {
                const s = bloc.statsInternes.find((x) => x.niveau === c.niveau);
                const manque = Math.max(0, pointsRequis - (s?.n ?? 0));
                return <span style={{ color: manque ? 'var(--amber-600)' : undefined, fontWeight: manque ? 700 : 400 }} title={manque ? `${manque} point(s) manquant(s) sur les ${pointsRequis} requis` : undefined}>{s?.n ?? 0}</span>;
              },
            },
            { cle: 'moyenne', libelle: 'Moyenne', type: 'nombre', valeur: (c) => bloc.statsInternes.find((x) => x.niveau === c.niveau)?.moyenne ?? null, rendu: (c) => { const s = bloc.statsInternes.find((x) => x.niveau === c.niveau); return s?.n ? s.moyenne : '—'; } },
            {
              cle: 'sdObs', libelle: 'σ obs.', type: 'nombre', valeur: (c) => bloc.statsInternes.find((x) => x.niveau === c.niveau)?.sd ?? null,
              rendu: (c) => { const s = bloc.statsInternes.find((x) => x.niveau === c.niveau); return <span style={{ color: s && s.sd > c.sd ? 'var(--red-600)' : undefined }}>{s?.n ? s.sd : '—'}</span>; },
            },
            { cle: 'cv', libelle: 'CV %', type: 'nombre', valeur: (c) => bloc.statsInternes.find((x) => x.niveau === c.niveau)?.cv ?? null, rendu: (c) => { const s = bloc.statsInternes.find((x) => x.niveau === c.niveau); return s?.n ? s.cv.toFixed(2) : '—'; } },
            {
              cle: 'biais', libelle: 'Biais (σ fourn.)', type: 'nombre', valeur: (c) => (figee ? bloc.approuveeDetails : rec?.details)?.find((x) => x.niveau === c.niveau)?.biaisSigma ?? null,
              rendu: (c) => {
                const d = (figee ? bloc.approuveeDetails : rec?.details)?.find((x) => x.niveau === c.niveau);
                return <span style={{ color: d && d.biaisSigma !== null && Math.abs(d.biaisSigma) > 2 ? 'var(--red-600)' : undefined }}>{d && d.biaisSigma !== null ? `${d.biaisSigma > 0 ? '+' : ''}${d.biaisSigma}` : '—'}</span>;
              },
            },
            ...(figee ? [{
              cle: 'figee', libelle: 'Figée (cible / σ / n)', valeur: (c: typeof bloc.cibles[number]) => (bloc.ciblesInternesFigees || []).find((x) => x.niveau === c.niveau)?.cible ?? null,
              style: { color: 'var(--cyan-700)', fontWeight: 700 },
              rendu: (c: typeof bloc.cibles[number]) => { const f = (bloc.ciblesInternesFigees || []).find((x) => x.niveau === c.niveau); return f ? `${f.cible} / ${f.sd} / ${f.n}` : '—'; },
            }] : []),
          ]}
        />
        {!figee && rec && !rec.recevable && (
          <div style={{ padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--amber-50)', border: '1px solid var(--amber-300)', display: 'grid', gap: 3 }}>
            <strong style={{ fontSize: 12.5, color: 'var(--text-strong)' }}>Approbation impossible pour l’instant</strong>
            {rec.motifs.map((m, i) => <span key={i} style={{ fontSize: 12.5, color: 'var(--text-body)' }}>· {m}</span>)}
          </div>
        )}
        {!figee && bloc.baseline && rec?.recevable && (
          <label style={{ display: 'grid', gap: 4, fontSize: 12.5 }}>
            {/* R2 — l'approbation rejuge tous les points de baseline : le motif
                est OBLIGATOIRE dès qu'il y en a, il s'écrit sur chacun d'eux. */}
            <span style={{ fontWeight: 600 }}>
              Motif de l’approbation{bloc.points.length ? ` — obligatoire : les ${bloc.points.length} point(s) de baseline reçoivent leur verdict et portent ce motif` : ' (facultatif, va au journal)'}
            </span>
            <textarea value={commentaire} onChange={(e) => setCommentaire(e.target.value)} rows={2}
              style={{ ...inputStyle, height: 'auto', fontSize: 12.5 }} placeholder="Ex. : série de constitution revue le 07/10, aucun point exclu" />
          </label>
        )}
      </div>
    </Modale>
  );
}

/** Levey-Jennings. Axe vertical en σ : les niveaux ont des cibles très
 *  différentes (une créatinine à 185 et une à 509), seule la normalisation
 *  permet de les superposer. */
export function LeveyJennings({ blocs, nbNiveaux, onPoint, selection, nomOperateur: nomFourni, unite, libelles,
  niveauxVisibles, zoom, rejetAffichage = true, provisoires }: {
  blocs: Bloc[]; nbNiveaux: number;
  onPoint: (p: Point | null) => void; selection: Point | null;
  /** Comment NOMMER l'opérateur d'un point dans l'infobulle.
   *
   *  ⚠️ OPTIONNEL, ET LE DÉFAUT EST LE BON (11/10/2026). Quatre appelants
   *  passaient `(k) => k` — la fonction identité — et affichaient donc la CLÉ
   *  TECHNIQUE (« tech.belhadjsalah ») dans l'infobulle. Un paramètre
   *  obligatoire qu'on peut satisfaire avec l'identité n'est pas une garde :
   *  à défaut, on nomme la personne, et il n'y a plus rien à oublier. */
  nomOperateur?: (k: string) => string;
  unite: string | null; libelles: string[];
  /** Les niveaux à tracer (RQ, 18/08 : « choisir si j'affiche 1 seul niveau ou
   *  plusieurs »). Superposer trois niveaux rend une dérive d'un seul illisible. */
  niveauxVisibles?: number[];
  /** La fenêtre TEMPORELLE affichée, en fraction [0,1] de l'étendue des dates.
   *  Le zoom porte sur le TEMPS et non sur l'image : agrandir des pixels ne
   *  sépare pas deux points du même jour, resserrer l'axe si. */
  zoom?: { debut: number; fin: number };
  /** `ged-setting.cqiImport.rejetAffichage` — un point marqué rejeté reste-t-il
   *  tracé ? Défaut `true` (jamais masqué) pour les appelants qui ne
   *  connaissent pas le réglage. L'exclusion des STATISTIQUES est déjà faite
   *  côté serveur (`etat.evalues`) ; ce filtre-ci ne touche que le dessin. */
  rejetAffichage?: boolean;
  /**
   * Les points qui NE SONT PAS ENCORE ENREGISTRÉS — identifiés par leur
   * `passageId` (12/09/2026).
   *
   * ── Pourquoi (RQ, 12/09/2026) ────────────────────────────────────────────
   *
   * « En routine, la détection de non-linéarité se fera directement sur la
   *  machine et la repasse automatique, mais 2-1s ou 2-2s ou 3-1s ne sera
   *  détectée qu'au moment du chargement des données sur l'outil. Donc à ce
   *  moment je veux voir le nouveau point, en couleur plus claire, jusqu'à sa
   *  validation. »
   *
   * C'est la raison d'être de la revue point par point : une règle de Westgard
   * ne se lit PAS sur un point isolé — 2-1s, 2-2s et 3-1s portent sur une
   * SUITE. Le tableau de l'aperçu ne pouvait donc pas la montrer ; seule la
   * courbe le peut, et seulement si le point candidat y figure.
   *
   * ⚠️ IL EST DESSINÉ, MAIS IL SE DISTINGUE : creux, cerclé de tirets. Le
   * rendre semblable aux autres ferait croire qu'il est acquis — c'est-à-dire
   * lui donner exactement le poids que la validation doit encore lui accorder.
   */
  provisoires?: string[];
}) {
  const settings = useGed((s) => s.settings);
  const nomOperateur = nomFourni || ((k: string) => userName(settings, k));
  const visibles = niveauxVisibles && niveauxVisibles.length
    ? niveauxVisibles : Array.from({ length: nbNiveaux }, (_, k) => k + 1);
  // N1-5 : la valeur cible et ses multiples de σ sont écrits EN FACE des lignes,
  // dans l'unité de mesure. Lire « +2σ » oblige à retourner au tableau pour
  // savoir ce que cela vaut ; c'est ce va-et-vient qu'on supprime. La marge de
  // droite s'élargit avec le nombre de niveaux : chacun a sa propre cible.
  const cibles = (blocs[blocs.length - 1]?.ciblesEffectives || []);
  const colonnes = cibles.filter((c) => (niveauxVisibles && niveauxVisibles.length
    ? niveauxVisibles.includes(c.niveau) : c.niveau <= nbNiveaux));
  const LARGEUR_COL = 62;
  const PADR = colonnes.length ? 10 + colonnes.length * LARGEUR_COL : 14;
  const W = 900 + (colonnes.length ? colonnes.length * LARGEUR_COL : 0), H = 210, PADX = 40, PADY = 16;
  /** Chiffres significatifs suivant l'ordre de grandeur : une créatinine à 0.29
   *  et une ASAT à 402 ne se lisent pas avec la même précision. */
  const fmt = (v: number) => {
    const a = Math.abs(v);
    return a >= 100 ? v.toFixed(0) : a >= 10 ? v.toFixed(1) : a >= 1 ? v.toFixed(2) : v.toFixed(3);
  };
  // Points de tous les blocs, à la suite, avec la frontière de lot.
  const suite = blocs.flatMap((b, bi) => b.points.map((p) => ({ p, bi, lot: b.lotControle })));
  // Axe temporel RÉEL (J4) : la distance entre deux points reflète l'écart de
  // dates, pas leur rang. Un contrôle passé le même jour à 8 h et un autre trois
  // jours plus tard ne doivent pas paraître équidistants d'un troisième passé la
  // veille — c'est ce qui rend une dérive lisible. `datePassage` ne porte que le
  // jour (pas d'heure en base) : les passages du même jour reçoivent un petit
  // décalage pour ne pas se superposer exactement (recalibration → repassage).
  const temps = suite.map((s) => new Date(s.p.date).getTime());
  const t0 = temps.length ? Math.min(...temps) : 0;
  const t1 = temps.length ? Math.max(...temps) : 1;
  /* La fenêtre de zoom découpe l'ÉTENDUE DES DATES, pas la liste des points :
     un intervalle vide reste vide à l'écran — c'est une information (aucun
     contrôle passé cette semaine-là), pas un défaut d'affichage. */
  const etendue = Math.max(1, t1 - t0);
  const tMin = t0 + etendue * (zoom?.debut ?? 0);
  const tMax = t0 + etendue * (zoom?.fin ?? 1);
  const spanJours = Math.max(1, tMax - tMin);
  const parJour = new Map<string, number[]>();
  suite.forEach((s, i) => { const g = parJour.get(s.p.date) || []; g.push(i); parJour.set(s.p.date, g); });
  const xArr = suite.map((s, i) => {
    const base = PADX + ((temps[i] - tMin) / spanJours) * (W - PADX - PADR);
    const groupe = parJour.get(s.p.date) || [i];
    if (groupe.length <= 1) return base;
    return base + (groupe.indexOf(i) - (groupe.length - 1) / 2) * 5;
  });
  const x = (i: number) => xArr[i] ?? PADX;

  /* ── LES CHANGEMENTS DE LOT DE RÉACTIF (12/09/2026) ──────────────────────
     Calculés ICI et non dans le JSX : la première version portait un `let`
     réassigné à l'intérieur du `map` de rendu, ce que le compilateur React
     refuse (« Cannot reassign variable after render completes ») — et à juste
     titre, une dérivation qui se souvient d'un rendu à l'autre n'est plus une
     dérivation.

     Les trois règles qu'elle applique — on ne marque que les CHANGEMENTS, un
     passage sans lot n'interrompt rien, un retour à un lot déjà vu compte —
     sont écrites et éprouvées dans `lib/changements-lot.ts`. */
  const changementsReactif = changementsDeLot(suite.map((s) => s.p));
  const y = (z: number) => H / 2 - (Math.max(-3.6, Math.min(3.6, z)) / 3.6) * (H / 2 - PADY);

  /* R1 — pendant la BASELINE, les repères sont PROVISOIRES : la cible qui les
     porte est celle du fournisseur en repli, aucune règle n'est évaluée. On les
     trace en tirets longs, atténués, et on l'écrit sur le graphe. */
  const baseline = blocs[blocs.length - 1]?.baseline === true;
  const reperes = [
    { z: 0, c: 'var(--navy-800)', d: baseline ? '8 5' : '' },
    { z: 1, c: 'var(--green-500)', d: baseline ? '8 5' : '4 4' }, { z: -1, c: 'var(--green-500)', d: baseline ? '8 5' : '4 4' },
    { z: 2, c: 'var(--amber-500)', d: baseline ? '8 5' : '4 4' }, { z: -2, c: 'var(--amber-500)', d: baseline ? '8 5' : '4 4' },
    { z: 3, c: 'var(--red-500)', d: baseline ? '8 5' : '2 3' }, { z: -3, c: 'var(--red-500)', d: baseline ? '8 5' : '2 3' },
  ];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img"
      aria-label={`Carte de Levey-Jennings — ${suite.length} passages`}>
      {baseline && (
        <text x={PADX + 4} y={PADY + 9} fontSize={9} fontWeight={700} fontFamily="var(--font-mono)" fill="var(--cyan-700)">
          baseline en cours — repères provisoires, aucun verdict
        </text>
      )}
      {reperes.map((r) => (
        <g key={r.z}>
          <line x1={PADX} y1={y(r.z)} x2={W - PADR} y2={y(r.z)} stroke={r.c} strokeWidth={r.z === 0 ? 1.4 : 1} strokeDasharray={r.d} opacity={baseline ? 0.3 : 0.5} />
          <text x={2} y={y(r.z) + 3.5} fontSize={9} fontFamily="var(--font-mono)" fill={r.c}>
            {r.z === 0 ? 'cible' : `${r.z > 0 ? '+' : ''}${r.z}σ`}
          </text>
        </g>
      ))}

      {/* N1-5 — ce que 1, 2, 3 σ VALENT, niveau par niveau, dans l'unité. */}
      {colonnes.map((c, ci) => {
        const xc = W - PADR + 10 + ci * LARGEUR_COL;
        const couleur = COULEURS[ci % COULEURS.length];
        return (
          <g key={`col${c.niveau}`}>
            <text x={xc} y={PADY - 5} fontSize={8.5} fontWeight={700} fontFamily="var(--font-mono)" fill={couleur}>
              {libelles?.[c.niveau - 1] || `N${c.niveau}`}
            </text>
            {reperes.map((r) => (
              <text key={r.z} x={xc} y={y(r.z) + 3.5} fontSize={8.5} fontFamily="var(--font-mono)"
                fill={r.z === 0 ? 'var(--text-strong)' : 'var(--text-muted)'} fontWeight={r.z === 0 ? 700 : 400}>
                {fmt(c.cible + r.z * c.sd)}
              </text>
            ))}
            {/* La provenance de la cible est écrite SUR le graphe : une courbe
                tracée sur une cible interne ne doit pas se lire comme une
                courbe tracée sur le certificat du fournisseur. */}
            <text x={xc} y={H - 3} fontSize={7.5} fontFamily="var(--font-mono)"
              fill={c.source === 'interne' ? 'var(--cyan-700)' : 'var(--text-muted)'}>
              {c.source === 'interne' ? 'interne' : 'fourn.'}
            </text>
          </g>
        );
      })}
      {unite && colonnes.length > 0 && (
        <text x={W - PADR + 10} y={H - 12} fontSize={7.5} fontFamily="var(--font-mono)" fill="var(--text-muted)">{unite}</text>
      )}

      {/* Frontières de lot de CONTRÔLE : un décrochage au changement de lot
          doit se voir. Elles ne se dessinent qu'en mode « période », où
          plusieurs lots coexistent — sur un seul lot, il n'y a pas de
          frontière. */}
      {suite.map((s, i) => (i > 0 && s.bi !== suite[i - 1].bi ? (
        <g key={`f${i}`}>
          <line x1={x(i) - 2} y1={PADY / 2} x2={x(i) - 2} y2={H - PADY / 2} stroke="var(--navy-800)" strokeWidth={1} strokeDasharray="3 3" opacity={0.55} />
          <text x={x(i) + 2} y={PADY - 4} fontSize={8.5} fontFamily="var(--font-mono)" fill="var(--text-muted)">lot {s.lot || '?'}</text>
        </g>
      ) : null))}

      {/* ── CHANGEMENT DE LOT DE RÉACTIF (12/09/2026) ───────────────────────
          « La carte de CQI change totalement quand on a un changement de lot du
           contrôle. Par contre, au moment du changement du lot de réactif, on
           fait juste un nouveau passage de CQI et on continue sur la même
           carte. Je voudrais que tu affiches le début d'un nouveau lot de
           réactifs par un trait vertical sur la courbe. » — RQ, 11/09.

          ⚠️ DEUX TRAITS QUI NE DISENT PAS LA MÊME CHOSE, d'où deux apparences.
          La frontière de lot de CONTRÔLE est pleine hauteur, sombre, tiretée
          court : de part et d'autre, les points ne sont plus comparables sur la
          même cible. Celle du RÉACTIF est ambre et n'occupe que la hauteur du
          nuage : la carte continue, c'est une CAUSE POSSIBLE qu'on note, pas
          une rupture de référentiel. Les rendre semblables ferait lire un
          changement de cible là où il n'y en a pas.

          ⚠️ LE PREMIER LOT N'EST PAS UNE FRONTIÈRE. On ne marque que les
          CHANGEMENTS : un trait sur le premier point dirait « ici commence un
          nouveau lot » alors qu'on ne sait rien d'avant.

          ⚠️ UN PASSAGE SANS LOT N'INTERROMPT RIEN. La colonne est vide sur la
          plupart des passages repris ; traiter `null` comme une valeur ferait
          deux traits à chaque trou — « fin du lot », « retour du lot » — pour
          une information qu'on n'a simplement pas. */}
      {changementsReactif.map(({ i, lot, date }) => (
        <g key={`r${i}`}>
          <line x1={x(i) - 2} y1={PADY} x2={x(i) - 2} y2={H - PADY}
            stroke="var(--amber-500)" strokeWidth={1.2} strokeDasharray="1 3" opacity={0.85}>
            {/* Le survol dit le lot : c'est ce que la RQ demandait, et c'est
                aussi tout ce qu'il y a à dire — l'inscrire en clair sur la
                courbe la surchargerait sans rien y ajouter. */}
            <title>Nouveau lot de réactif : {lot} — depuis le {fmtDate(date)}</title>
          </line>
          <circle cx={x(i) - 2} cy={PADY} r={2.6} fill="var(--amber-500)">
            <title>Nouveau lot de réactif : {lot} — depuis le {fmtDate(date)}</title>
          </circle>
        </g>
      ))}

      {/* Repères de dates — l'axe est maintenant temporel, ces deux dates en
          bornent l'échelle. */}
      {suite.length > 0 && (
        <>
          <text x={x(0)} y={H - 3} fontSize={8.5} fontFamily="var(--font-mono)" fill="var(--text-muted)" textAnchor="start">
            {fmtDate(suite[0].p.date)}
          </text>
          <text x={x(suite.length - 1)} y={H - 3} fontSize={8.5} fontFamily="var(--font-mono)" fill="var(--text-muted)" textAnchor="end">
            {fmtDate(suite[suite.length - 1].p.date)}
          </text>
        </>
      )}

      {visibles.map((niveau) => {
        // La COULEUR suit le rang du niveau, pas sa position dans la sélection :
        // afficher N2 seul ne doit pas lui donner la couleur de N1.
        const couleur = COULEURS[(niveau - 1) % COULEURS.length];
        const pts = suite
          .map((s, i) => ({ i, v: s.p.valeurs.find((v) => Number(v.niveau) === niveau) }))
          .filter((o) => o.v && typeof o.v.z === 'number')
          .filter((o) => temps[o.i] >= tMin && temps[o.i] <= tMax)
          // Filtrage d'AFFICHAGE seulement — l'exclusion des stats est déjà
          // faite côté serveur ; un point rejeté peut donc rester sur le
          // graphe (barré) sans rien changer à la cible ni à l'écart-type.
          .filter((o) => rejetAffichage || !o.v!.rejete);
        return (
          <g key={niveau}>
            {/* La ligne s'arrête AVANT les points provisoires et reprend en
                tirets jusqu'à eux : le trait plein relie ce qui est acquis, et
                l'on voit où la série cesse d'être établie. */}
            <polyline points={pts.filter((o) => !provisoires?.includes(suite[o.i].p.passageId))
              .map((o) => `${x(o.i)},${y(o.v!.z as number)}`).join(' ')}
              fill="none" stroke={couleur} strokeWidth={1.4} opacity={0.85} />
            {!!provisoires?.length && (
              <polyline points={pts.map((o) => `${x(o.i)},${y(o.v!.z as number)}`).join(' ')}
                fill="none" stroke={couleur} strokeWidth={1.2} opacity={0.5} strokeDasharray="3 3" />
            )}
            {pts.map((o) => {
              const s = suite[o.i];
              const z = o.v!.z as number;
              const sel = selection?.passageId === s.p.passageId;
              const grave = s.p.couleur === 'rouge';
              const alerte = s.p.couleur === 'jaune';
              const rejete = o.v!.rejete === true;
              /* ⚠️ PAS ENCORE ENREGISTRÉ. Le point est tracé — sans quoi on ne
                 verrait pas la règle qu'il déclenche, ce qui est tout l'objet
                 de la revue — mais il est CREUX et cerclé de tirets : on lit
                 « candidat », pas « acquis ». */
              const provisoire = !!provisoires?.includes(s.p.passageId);
              return (
                <g key={`${niveau}-${o.i}`} style={{ cursor: 'pointer', opacity: rejete ? 0.4 : 1 }} onClick={() => onPoint(sel ? null : s.p)}>
                  <title>
                    {`N${niveau} · ${o.v!.valeur} · ${fmtDate(s.p.date)}${s.p.numeroSerie ? ` · série n°${s.p.numeroSerie}` : ''} · ${z >= 0 ? '+' : ''}${z.toFixed(2)}σ`
                      + (s.p.operateur ? `\n👤 ${nomOperateur(s.p.operateur)}` : '')
                      + (s.p.regles.length ? `\n⚠ ${s.p.regles.map((r) => r.code).join(', ')}` : '')
                      + (s.p.repasse ? '\n↻ repassé dans la même journée' : '')
                      + (s.p.suite === 'derogation' ? '\n⚖ dérogation' : '')
                      + (s.p.enAttente ? `\n⏳ ${s.p.exige || 'en attente d’une suite'}` : '')
                      + (s.p.commentaire ? `\n💬 ${s.p.commentaire}` : '')
                      + (rejete ? `\n⛔ point rejeté${o.v!.rejeteMotif ? ` — ${o.v!.rejeteMotif}` : ''}` : '')
                      + (provisoire ? '\n⧗ pas encore enregistré — en cours de revue' : '')
                      + (s.p.verdictRevise ? `\n↺ verdict révisé : ${s.p.statutOrigine} à l’origine → ${s.p.statut} aujourd’hui`
                        + (s.p.reevaluations?.length ? ` — ${fmtDate(s.p.reevaluations[s.p.reevaluations.length - 1]!.le)} : ${s.p.reevaluations[s.p.reevaluations.length - 1]!.motif}` : '') : '')}
                  </title>
                  <circle cx={x(o.i)} cy={y(z)} r={9} fill="transparent" />
                  {/* R2 — verdict RÉVISÉ : l'origine diffère du verdict courant.
                      Un anneau cyan pointillé, et le détail au survol. */}
                  {s.p.verdictRevise && (
                    <circle cx={x(o.i)} cy={y(z)} r={sel ? 10 : 8.5} fill="none" stroke="var(--cyan-600)" strokeWidth={1.2} strokeDasharray="1.5 2" />
                  )}
                  {(grave || alerte) && (
                    <circle cx={x(o.i)} cy={y(z)} r={sel ? 7.5 : 6}
                      fill="none" stroke={grave ? 'var(--red-500)' : 'var(--amber-500)'} strokeWidth={1.6}
                      // En attente d'une suite : anneau plein. Soldé (dérogation
                      // ou repasse) : anneau pointillé — le point reste non
                      // conforme, mais il n'attend plus rien de personne.
                      strokeDasharray={s.p.enAttente ? undefined : '2 2'} />
                  )}
                  <circle cx={x(o.i)} cy={y(z)} r={sel ? 4.4 : 3}
                    fill={provisoire ? 'var(--surface-card)'
                      : grave ? 'var(--red-500)' : alerte ? 'var(--amber-500)' : couleur}
                    stroke={provisoire ? (grave ? 'var(--red-500)' : alerte ? 'var(--amber-500)' : couleur) : 'var(--surface-card)'}
                    strokeWidth={provisoire ? 1.6 : 0.9}
                    strokeDasharray={provisoire ? '2 1.5' : undefined} />
                  {/* Rejeté : une barre en travers, comme un mot rayé — le
                      point reste là, mais lu comme « écarté », pas « bon ». */}
                  {rejete && (
                    <line x1={x(o.i) - 5} y1={y(z) - 5} x2={x(o.i) + 5} y2={y(z) + 5}
                      stroke="var(--text-strong)" strokeWidth={1.3} />
                  )}
                </g>
              );
            })}
          </g>
        );
      })}
    </svg>
  );
}

/** Performance analytique : ce que le laboratoire observe, face à ce que le
 *  fournisseur annonce. C'est la donnée d'entrée de la revue périodique. */
/** Le tableau de performance sous le graphe.
 *
 *  ⚠️ Il écrivait « Niveau 1 », « Niveau 2 » EN DUR alors que la carte porte
 *  des libellés (`libellesNiveaux`) — le graphe, lui, les employait déjà.
 *  Renommer un niveau changeait donc la courbe et pas le tableau : deux noms
 *  pour la même chose sur le même écran, et l'impression que le réglage n'a
 *  pas pris (retour Ines du 17/08). */
/** Modification du critère d'un niveau qualitatif du lot OUVERT — inline, dans la ligne du niveau. */
function CritereQualitatif({ carteId, cible, unite, onFait }: {
  carteId: string; cible: Cible; unite: string | null; onFait: (message: string) => void;
}) {
  const [edition, setEdition] = useState(false);
  const [attendu, setAttendu] = useState<'inferieur' | 'superieur'>(cible.attendu === 'superieur' ? 'superieur' : 'inferieur');
  const [limite, setLimite] = useState(String(cible.limite ?? ''));
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();
  const { demanderTexte, confirmation } = useConfirmation();
  const enregistrer = () => run('critere', async () => {
    // R2 — aperçu d'impact, motif si des points changent de verdict.
    const r = await ecrireAvecApercu(`/api/ged/ged/cqi-cartes/${carteId}/critere`, { niveau: cible.niveau, attendu, limite }, demanderTexte);
    if (!r) return;
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { setErreur(j.error?.message || 'Modification refusée.'); return; }
    setErreur(''); setEdition(false);
    onFait(j.inchange ? 'Critère inchangé.'
      : `Critère modifié${j.rejuges ? ` — ${j.rejuges} point(s) changent de verdict` : ' — aucun point ne change de verdict'}.`);
  });
  if (!edition) {
    return (
      <span>
        critère : {cible.attendu === 'superieur' ? '≥' : '<'} {cible.limite}{unite ? ` ${unite}` : ''}{' '}
        <Bouton taille="sm" onClick={() => setEdition(true)} aria-label={`Modifier le critère du niveau ${cible.niveau}`}>Modifier</Bouton>
      </span>
    );
  }
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
      <select aria-label={`Sens du critère niveau ${cible.niveau}`} value={attendu}
        onChange={(e) => setAttendu(e.target.value === 'superieur' ? 'superieur' : 'inferieur')} style={{ ...inputStyle, width: 150 }}>
        <option value="inferieur">inférieur à</option>
        <option value="superieur">supérieur ou égal à</option>
      </select>
      <input aria-label={`Limite du critère niveau ${cible.niveau}`} value={limite} onChange={(e) => setLimite(e.target.value)} style={{ ...inputStyle, width: 70 }} />
      <Bouton variante="primaire" taille="sm" onClick={() => void enregistrer()} disabled={estOccupe()}>{estOccupe('critere') ? '…' : 'Enregistrer'}</Bouton>
      <Bouton taille="sm" onClick={() => { setEdition(false); setErreur(''); }}>Annuler</Bouton>
      {erreur && <span style={{ color: 'var(--red-600)', fontSize: 12 }}>{erreur}</span>}
      {confirmation}
    </span>
  );
}

function Performance({ bloc, unite, libelles = [], carteId, onCritereModifie }: {
  bloc: Bloc; unite: string | null; libelles?: string[]; carteId?: string; onCritereModifie?: (message: string) => void;
}) {
  /** Le libellé du niveau, ou son rang à défaut. Les niveaux sont numérotés à
   *  partir de 1 ; le tableau des libellés, à partir de 0. */
  const nomNiveau = (n: number) => (libelles[n - 1] || '').trim() || `Niveau ${n}`;
  if (!bloc.cibles.length) return null;
  // 06/09/2026 (Inès) : « les infos en dessous sont peu lisibles » — un mur de
  // huit colonnes abrégées, sans titre ni distinction visuelle entre ce
  // qu'ANNONCE le fournisseur et ce que le LABO observe. L'en-tête à deux
  // niveaux nomme les deux groupes une fois, au lieu de le répéter dans
  // chaque libellé de colonne (« Cible fourn. », « σ fourn. »…) ; le fond
  // légèrement teinté du groupe fournisseur fait la même distinction d'un
  // coup d'œil, sans avoir à relire les en-têtes.
  const teinteFourn = 'var(--surface-sunken)';
  return (
    <div>
      <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 6 }}>
        Performance par niveau
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="ged-tableau" ref={enTeteCollant} style={{ borderCollapse: 'collapse', fontSize: 13, minWidth: 560 }}>
          <thead>
            <tr style={{ color: 'var(--text-muted)', fontSize: 10.5, textTransform: 'uppercase' }}>
              <th scope="col"></th>
              <th scope="col" colSpan={2} style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', textAlign: 'center', background: teinteFourn, borderTopLeftRadius: 'var(--radius-sm)' }}>Fournisseur</th>
              <th scope="col" colSpan={4} style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', textAlign: 'center' }}>Observé au laboratoire</th>
              <th scope="col" style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', textAlign: 'center' }}>Cible appliquée</th>
            </tr>
            <tr style={{ color: 'var(--text-muted)', textAlign: 'right', borderBottom: '2px solid var(--border-default)' }}>
              <th scope="col" style={{ textAlign: 'left', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x) var(--tableau-ligne-padding-y) 0' }}>Niveau</th>
              <th scope="col" style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', background: teinteFourn }}>Cible</th>
              <th scope="col" style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', background: teinteFourn }}>σ</th>
              <th scope="col" style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>n</th>
              <th scope="col" style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>Moyenne</th>
              <th scope="col" style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>σ</th>
              <th scope="col" style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>CV %</th>
              <th scope="col" style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>Biais %</th>
              {/* Ce qui sert RÉELLEMENT à calculer les z. Sans cette colonne, rien
                  à l'écran ne distingue une carte basculée d'une carte qui ne
                  l'est pas — c'est exactement ce qui rendait N1-1 invisible. */}
              <th scope="col" style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}></th>
            </tr>
          </thead>
          <tbody style={{ fontFamily: 'var(--font-mono)' }}>
            {bloc.cibles.map((c) => {
              const s = bloc.statsInternes.find((x) => x.niveau === c.niveau);
              const eff = bloc.ciblesEffectives?.find((x) => x.niveau === c.niveau);
              const cvFourn = c.cible ? (c.sd / Math.abs(c.cible)) * 100 : null;
              // Un CV observé supérieur à celui du fournisseur signale une perte
              // de précision : c'est l'information qu'on vient chercher ici.
              const cvPire = s && cvFourn !== null && s.cv > cvFourn;
              return (
                <tr key={c.niveau} style={{ borderTop: '1px solid var(--border-default)', textAlign: 'right' }}>
                  <td style={{ textAlign: 'left', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x) var(--tableau-ligne-padding-y) 0', fontFamily: 'var(--font-sans)', fontWeight: 700 }}>
                    {nomNiveau(c.niveau)}{c.lot ? <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> · lot {c.lot}</span> : null}
                  </td>
                  {c.qualitatif ? (
                    <td colSpan={2} style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', background: teinteFourn, textAlign: 'center' }}
                      title="Niveau qualitatif : jugé sur son critère d’acceptation, hors moyenne et règles de Westgard.">
                      {carteId && onCritereModifie && !bloc.cloturee
                        ? <CritereQualitatif carteId={carteId} cible={c} unite={unite} onFait={onCritereModifie} />
                        : <>critère : {c.attendu === 'superieur' ? '≥' : '<'} {c.limite}{unite ? ` ${unite}` : ''}</>}
                    </td>
                  ) : (<>
                  <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', background: teinteFourn }}>{c.cible}{unite ? ` ${unite}` : ''}</td>
                  <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', background: teinteFourn }}>{c.sd}</td>
                  </>)}
                  <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>{s?.n ?? 0}</td>
                  <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>{s?.n ? s.moyenne : '—'}</td>
                  <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>{s?.n ? s.sd : '—'}</td>
                  <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', color: cvPire ? 'var(--red-600)' : undefined, fontWeight: cvPire ? 700 : 400 }}>
                    {s?.n ? s.cv.toFixed(2) : '—'}
                    {cvFourn !== null && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> / {cvFourn.toFixed(2)}</span>}
                  </td>
                  <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>{s && s.biais !== null ? `${s.biais > 0 ? '+' : ''}${s.biais.toFixed(2)}` : '—'}</td>
                  <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>
                    {eff ? (
                      <span style={{ color: eff.source === 'interne' ? 'var(--cyan-700)' : undefined, fontWeight: eff.source === 'interne' ? 700 : 400 }}
                        title={eff.repli || undefined}>
                        {eff.cible} ± {eff.sd}
                        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 11.5, color: 'var(--text-muted)', marginLeft: 4 }}>
                          {eff.source === 'interne' ? 'interne' : 'fourn.'}
                        </span>
                      </span>
                    ) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 6 }}>
        CV : observé / annoncé par le fournisseur. Le biais mesure la justesse — il se calcule
        toujours contre la valeur assignée du fournisseur, même en cible interne.
      </div>
    </div>
  );
}

/** LE LOT EN COURS, ET SES INFORMATIONS (refonte CQI L1, 27/10/2026) — marque,
 *  référence, péremption, lien vers l'insert du fabricant, lot et code de
 *  niveau. Les compléter n'ouvre PAS de nouvelle période et ne change aucun
 *  verdict : les cibles, elles, ont leur propre chemin. Le lot par niveau est le
 *  lot MAÎTRE (85910), celui que la vérification des aliquots compare ; le code
 *  de niveau (85911 / 85912) est rangé à part. */
export function InfosLotCqi({ bloc, nbNiveaux, peutModifier, onFait }: {
  bloc: Bloc; nbNiveaux: number; peutModifier: boolean; onFait: () => void;
}) {
  const [ouvert, setOuvert] = useState(false);
  const lotDe = (n: number) => (bloc.lotsNiveaux || []).find((l) => String(l.niveau) === String(n));
  const initial = () => ({
    marqueControle: bloc.marqueControle || '', refControle: bloc.refControle || '',
    datePeremption: bloc.datePeremption || '', insertUrl: bloc.insertUrl || '',
    niveaux: Array.from({ length: nbNiveaux }, (_, i) => ({
      numeroLot: lotDe(i + 1)?.numeroLot || bloc.lotControle || '', codeNiveau: lotDe(i + 1)?.codeNiveau || '',
    })),
  });
  const [f, setF] = useState(initial);
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();
  const lienBioRad = bloc.lotControle ? `https://myeinserts-app.qcnet.com/home/${encodeURIComponent(bloc.lotControle.trim())}` : '';

  const resume = [
    bloc.marqueControle, bloc.refControle ? `réf. ${bloc.refControle}` : null,
    bloc.datePeremption ? `périme le ${fmtDate(bloc.datePeremption)}` : null,
    ...(bloc.lotsNiveaux || []).map((l) => `N${l.niveau} ${l.codeNiveau || l.numeroLot}`),
  ].filter(Boolean).join(' · ');

  if (!ouvert) {
    return (
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5, color: 'var(--text-body)' }}>
        <span><strong>Lot en cours {bloc.lotControle || '—'}</strong>{resume ? ` — ${resume}` : ' — marque, référence et codes de niveau non renseignés'}</span>
        {bloc.insertUrl && <a href={bloc.insertUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary-active)', fontWeight: 600 }}>Insert du fabricant ↗</a>}
        {peutModifier && <Bouton variante="contour" taille="sm" onClick={() => { setF(initial()); setErreur(''); setOuvert(true); }}>Compléter le lot</Bouton>}
      </div>
    );
  }

  const enregistrer = () => run('infos', async () => {
    setErreur('');
    const r = await fetchEcriture(`/api/ged/ged/cqi-periodes/${bloc.periodeId}/infos`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        marqueControle: f.marqueControle, refControle: f.refControle, datePeremption: f.datePeremption, insertUrl: f.insertUrl,
        lotsNiveaux: f.niveaux.map((n, i) => ({ niveau: String(i + 1), numeroLot: n.numeroLot, codeNiveau: n.codeNiveau, flaconRef: lotDe(i + 1)?.flaconRef }))
          .filter((n) => n.numeroLot.trim()),
      }),
    });
    if (!r.ok) { setErreur((await r.json().catch(() => ({}))).error?.message || 'Enregistrement refusé.'); return; }
    setOuvert(false);
    onFait();
  });

  const lab = { display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 } as const;
  return (
    <div style={{ background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: 12, display: 'grid', gap: 10 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Compléter le lot {bloc.lotControle || ''}</div>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Ces informations ne changent ni les cibles ni aucun verdict. Pour changer une cible, ouvrez un nouveau lot ou demandez une cible interne.</div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 140px' }}><label style={lab} htmlFor="il-marque">Marque</label>
          <input id="il-marque" value={f.marqueControle} onChange={(e) => setF({ ...f, marqueControle: e.target.value })} style={inputStyle} /></div>
        <div style={{ flex: '1 1 120px' }}><label style={lab} htmlFor="il-ref">Référence</label>
          <input id="il-ref" value={f.refControle} onChange={(e) => setF({ ...f, refControle: e.target.value })} style={inputStyle} /></div>
        <div style={{ flex: '1 1 140px' }}><label style={lab} htmlFor="il-perim">Péremption du lot</label>
          <input id="il-perim" type="date" value={f.datePeremption} onChange={(e) => setF({ ...f, datePeremption: e.target.value })} style={inputStyle} /></div>
      </div>
      <div>
        <label style={lab} htmlFor="il-insert">Lien vers l’insert du fabricant</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input id="il-insert" value={f.insertUrl} placeholder="https://…" onChange={(e) => setF({ ...f, insertUrl: e.target.value })} style={{ ...inputStyle, flex: '1 1 260px' }} />
          {lienBioRad && f.insertUrl !== lienBioRad && (
            <Bouton variante="contour" taille="sm" onClick={() => setF({ ...f, insertUrl: lienBioRad })}>Lien Bio-Rad (myeInserts)</Bouton>
          )}
        </div>
      </div>
      {/* Une ligne de champs par niveau — une saisie, pas un registre. */}
      <div style={{ display: 'grid', gap: 8 }}>
        {f.niveaux.map((n, i) => (
          <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: 12.5, minWidth: 28, paddingBottom: 8 }}>N{i + 1}</span>
            <div style={{ flex: '1 1 160px' }}><label style={lab} htmlFor={`il-lot-${i}`}>Lot du contrôle (boîte)</label>
              <input id={`il-lot-${i}`} value={n.numeroLot} onChange={(e) => setF({ ...f, niveaux: f.niveaux.map((x, j) => (j === i ? { ...x, numeroLot: e.target.value } : x)) })} style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} /></div>
            <div style={{ flex: '1 1 160px' }}><label style={lab} htmlFor={`il-code-${i}`}>Code du niveau (sur le flacon)</label>
              <input id={`il-code-${i}`} value={n.codeNiveau} placeholder="ex. 85911" onChange={(e) => setF({ ...f, niveaux: f.niveaux.map((x, j) => (j === i ? { ...x, codeNiveau: e.target.value } : x)) })} style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} /></div>
          </div>
        ))}
      </div>
      {erreur && <div role="alert" style={{ fontSize: 12.5, color: 'var(--red-600)' }}>{erreur}</div>}
      <PiedActions taille="sm" annuler={{ onClick: () => setOuvert(false) }}
        principale={{ libelle: 'Enregistrer', libelleChargement: 'Enregistrement…', chargement: estOccupe('infos'), disabled: estOccupe(), onClick: () => void enregistrer() }} />
    </div>
  );
}

/** Ce que le moteur rend de l'insert Bio-Rad d'un lot (refonte CQI L2). */
type InsertBioRad = {
  lot: { catalogue: string; produit: string; lot: string; peremption: string | null; codesNiveaux: string[]; aDesCibles: boolean; depuisCodeNiveau: boolean };
  url: string; version: string | null; methodes: number;
  automate: { code: string; nom: string } | null;
  proposees: { analyte: string; methode: string; unite: string; renvois: string[]; sdIncertain: boolean;
    niveaux: { moyenne: number | null; bas: number | null; haut: number | null; sd: number | null }[] }[];
  renvois?: Record<string, string>; message: string | null;
};
/** Ce que l'insert a fourni au formulaire, pour le compléter après la création. */
type SourceInsert = { url: string; version: string | null; methode: string; codesNiveaux: string[] };

/** REMPLIR UN NOUVEAU LOT DEPUIS L'INSERT BIO-RAD (refonte CQI L2, 27/10/2026).
 *  Le moteur retrouve le lot chez Bio-Rad, lit l'insert et propose les lignes de
 *  l'automate de la carte ; on CHOISIT la ligne de son programme, et seulement
 *  alors le formulaire se remplit — rien n'est enregistré avant « Enregistrer
 *  le lot ». σ = plage / 6 (plages à ±3 σ) ; laissé vide quand l'insert ne
 *  permet pas de le déduire. */
function ImportInsertBioRad({ carteId, lotInitial, onAppliquer }: {
  carteId: string; lotInitial: string;
  onAppliquer: (r: InsertBioRad, ligne: InsertBioRad['proposees'][number]) => void;
}) {
  const [lot, setLot] = useState(lotInitial);
  const [res, setRes] = useState<InsertBioRad | null>(null);
  const [choix, setChoix] = useState(-1);
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();
  const chercher = () => run('insert', async () => {
    setErreur(''); setRes(null); setChoix(-1);
    const r = await fetch(`/api/ged/ged/inserts-fabricant/biorad/${encodeURIComponent(lot.trim())}?carteId=${encodeURIComponent(carteId)}`, { credentials: 'include' });
    const j = await r.json().catch(() => null);
    if (!r.ok) { setErreur(j?.error?.message || `Lecture refusée (HTTP ${r.status}).`); return; }
    setRes(j as InsertBioRad);
    if ((j as InsertBioRad).proposees.length === 1) setChoix(0);
  });
  const fmt = (x: number | null) => (x === null ? '—' : String(x).replace('.', ','));
  return (
    <div style={{ border: '1px dashed var(--border-strong)', borderRadius: 'var(--radius-md)', padding: 10, display: 'grid', gap: 8 }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)' }}>Remplir depuis l’insert Bio-Rad</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input aria-label="Numéro de lot Bio-Rad" value={lot} placeholder="ex. 85910 (ou un code de niveau)" onChange={(e) => setLot(e.target.value)}
          style={{ ...inputStyle, flex: '0 1 220px', fontFamily: 'var(--font-mono)' }} />
        <Bouton variante="contour" taille="sm" onClick={() => void chercher()} disabled={!lot.trim() || estOccupe()}>
          {estOccupe('insert') ? 'Lecture de l’insert…' : 'Chercher l’insert'}
        </Bouton>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Lu chez Bio-Rad (myeInserts) ; rien n’est enregistré à cette étape.</span>
      </div>
      {erreur && <div role="alert" style={{ fontSize: 12.5, color: 'var(--red-600)' }}>{erreur}</div>}
      {res && (
        <div style={{ display: 'grid', gap: 8, fontSize: 12.5 }}>
          <div>
            <strong>{res.lot.produit}</strong> (réf. {res.lot.catalogue}) — lot <span style={{ fontFamily: 'var(--font-mono)' }}>{res.lot.lot}</span>
            {res.lot.peremption ? `, périme le ${fmtDate(res.lot.peremption)}` : ''}
            {res.lot.codesNiveaux.length ? ` · codes de niveau ${res.lot.codesNiveaux.join(', ')}` : ''}
            {res.version ? ` · insert v${res.version}` : ''}
            {' · '}<a href={res.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary-active)', fontWeight: 600 }}>voir l’insert ↗</a>
          </div>
          {res.lot.depuisCodeNiveau && <div style={{ color: 'var(--text-muted)' }}>Le numéro saisi est un code de niveau : le lot maître est {res.lot.lot}.</div>}
          {res.message && <div style={{ color: 'var(--amber-600)' }}>{res.message}</div>}
          {res.proposees.length > 0 && (
            <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 6 }}>
              <legend style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Choisissez la ligne de VOTRE programme ({res.automate?.nom}) :</legend>
              {res.proposees.map((p, i) => (
                <label key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer', padding: '6px 8px', borderRadius: 'var(--radius-sm)', background: choix === i ? 'var(--cyan-50)' : 'transparent' }}>
                  <input type="radio" name="ligne-insert" checked={choix === i} onChange={() => setChoix(i)} style={{ marginTop: 3 }} />
                  <span>
                    <span style={{ fontWeight: 600 }}>{p.methode}</span>
                    <span style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                      {p.niveaux.map((n, k) => `N${k + 1} ${fmt(n.moyenne)} (${fmt(n.bas)}–${fmt(n.haut)}) σ ${fmt(n.sd)}`).join('   ·   ')} {p.unite}
                    </span>
                    {p.sdIncertain && <span style={{ display: 'block', color: 'var(--amber-600)' }}>σ non déductible de la plage pour cette ligne : à saisir à la main.</span>}
                  </span>
                </label>
              ))}
              <Bouton variante="primaire" taille="sm" style={{ width: 'fit-content' }} disabled={choix < 0}
                onClick={() => { const l = res.proposees[choix]; if (l) onAppliquer(res, l); }}>
                Remplir le formulaire avec cette ligne
              </Bouton>
            </fieldset>
          )}
        </div>
      )}
    </div>
  );
}

/** Ouvre un nouveau lot de contrôle : cible et écart-type fournisseur, un par
 *  niveau. C'est la seule façon de sortir une carte de l'état « sans cible » —
 *  jusqu'ici ces valeurs ne pouvaient venir que d'un script de données, jamais
 *  d'un geste dans l'écran. Un lot en remplace un autre : la sélection de
 *  fenêtre bascule sur « le lot en cours » après l'enregistrement pour montrer
 *  le résultat immédiatement. */
export function NouveauLotCqi({ carteId, nbNiveaux, unite, onCree }: {
  carteId: string; nbNiveaux: number; unite: string | null; onCree: () => void;
}) {
  const [ouvert, setOuvert] = useState(false);
  const [lotControle, setLotControle] = useState('');
  const [marqueControle, setMarqueControle] = useState('');
  const [refControle, setRefControle] = useState('');
  const [dateDebut, setDateDebut] = useState(() => aujourdhuiIso());
  const [datePeremption, setDatePeremption] = useState('');
  const [cibles, setCibles] = useState<Record<number, { cible: string; sd: string }>>({});
  /* L2 — ce que l'insert Bio-Rad a fourni ; complété sur la période après sa création. */
  const [source, setSource] = useState<SourceInsert | null>(null);
  const appliquerInsert = (r: InsertBioRad, l: InsertBioRad['proposees'][number]) => {
    setLotControle(r.lot.lot); setMarqueControle('Bio-Rad'); setRefControle(r.lot.catalogue);
    if (r.lot.peremption) setDatePeremption(r.lot.peremption);
    setCibles(Object.fromEntries(l.niveaux.slice(0, nbNiveaux).map((n, i) => [i + 1, {
      cible: n.moyenne !== null ? String(n.moyenne) : '', sd: n.sd !== null && !l.sdIncertain ? String(n.sd) : '',
    }])));
    setSource({ url: r.url, version: r.version, methode: l.methode, codesNiveaux: r.lot.codesNiveaux });
    setErreur('');
  };
  /* Niveau « à seuil » : le résultat est un texte (« < 2 »), jugé contre un critère, hors moyenne et Westgard. */
  const [seuils, setSeuils] = useState<Record<number, { actif: boolean; attendu: 'inferieur' | 'superieur'; limite: string }>>({});
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();

  if (!ouvert) {
    return (
      <Bouton variante="primaire" taille="sm" onClick={() => setOuvert(true)}>
        <I n="plus" s={13} />Nouveau lot de contrôle
      </Bouton>
    );
  }

  const valeur = (n: number, champ: 'cible' | 'sd') => cibles[n]?.[champ] || '';
  const majCible = (n: number, champ: 'cible' | 'sd', v: string) =>
    setCibles((c) => ({ ...c, [n]: { cible: c[n]?.cible || '', sd: c[n]?.sd || '', [champ]: v } }));

  const seuil = (n: number) => seuils[n] || { actif: false, attendu: 'inferieur' as const, limite: '' };
  const majSeuil = (n: number, patch: Partial<{ actif: boolean; attendu: 'inferieur' | 'superieur'; limite: string }>) =>
    setSeuils((c) => ({ ...c, [n]: { ...seuil(n), ...patch } }));

  const enregistrer = () => run('lot', async () => {
    if (!lotControle.trim()) { setErreur('Le numéro de lot est obligatoire.'); return; }
    if (!dateDebut) { setErreur('La date de début est obligatoire.'); return; }
    setErreur('');
    const r = await fetchEcriture(`/api/ged/ged/cqi-cartes/${carteId}/periode`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        lotControle: lotControle.trim(),
        marqueControle: marqueControle.trim() || undefined,
        refControle: refControle.trim() || undefined,
        dateDebut,
        datePeremption: datePeremption || undefined,
        cibles: Array.from({ length: nbNiveaux }, (_, i) => (seuil(i + 1).actif
          ? { niveau: i + 1, qualitatif: true, attendu: seuil(i + 1).attendu, limite: seuil(i + 1).limite }
          : { niveau: i + 1, cible: valeur(i + 1, 'cible'), sd: valeur(i + 1, 'sd') })),
      }),
    });
    if (!r.ok) { setErreur((await r.json().catch(() => ({}))).error?.message || 'Enregistrement refusé.'); return; }
    /* L2 — la période créée reçoit le lien de l'insert, les codes de niveau, et
       au journal la ligne d'où viennent les cibles. Un échec ici ne défait pas
       la période : il se dit, et « Compléter le lot » reste possible. */
    const cree = await r.json().catch(() => null);
    if (source && cree?.periodeId && source.url) {
      const r2 = await fetchEcriture(`/api/ged/ged/cqi-periodes/${cree.periodeId}/infos`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          insertUrl: source.url,
          lotsNiveaux: Array.from({ length: nbNiveaux }, (_, i) => ({ niveau: String(i + 1), numeroLot: lotControle.trim(), codeNiveau: source.codesNiveaux[i] || '' })),
          noteJournal: `cibles proposées par l’insert Bio-Rad${source.version ? ` v${source.version}` : ''}, ligne « ${source.methode} » (σ = plage / 6)`,
        }),
      });
      if (!r2.ok) { setErreur('Le lot est créé, mais le lien de l’insert n’a pas pu être enregistré : utilisez « Compléter le lot ».'); onCree(); return; }
    }
    setOuvert(false); setLotControle(''); setMarqueControle(''); setRefControle(''); setDatePeremption(''); setCibles({}); setSeuils({}); setSource(null);
    onCree();
  });

  return (
    <div style={{ background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: 12, display: 'grid', gap: 10 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Nouveau lot de contrôle</div>
      <ImportInsertBioRad carteId={carteId} lotInitial={lotControle} onAppliquer={appliquerInsert} />
      {source && (
        <div style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
          Rempli depuis l’insert Bio-Rad{source.version ? ` v${source.version}` : ''}, ligne « {source.methode} ». Vérifiez chaque valeur avant d’enregistrer : c’est vous qui validez.
        </div>
      )}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 160px' }}>
          <label style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }} htmlFor="lot-num">Numéro de lot *</label>
          <input id="lot-num" value={lotControle} onChange={(e) => setLotControle(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ flex: '1 1 140px' }}>
          <label style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }} htmlFor="lot-marque">Marque</label>
          <input id="lot-marque" value={marqueControle} onChange={(e) => setMarqueControle(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ flex: '1 1 140px' }}>
          <label style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }} htmlFor="lot-ref">Référence</label>
          <input id="lot-ref" value={refControle} onChange={(e) => setRefControle(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ flex: '1 1 140px' }}>
          <label style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }} htmlFor="lot-debut">Début *</label>
          <input id="lot-debut" type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ flex: '1 1 140px' }}>
          <label style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }} htmlFor="lot-perim">Péremption</label>
          <input id="lot-perim" type="date" value={datePeremption} onChange={(e) => setDatePeremption(e.target.value)} style={inputStyle} />
        </div>
      </div>
      <table className="ged-tableau" ref={enTeteCollant} style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
        <thead>
          <tr style={{ textAlign: 'left', color: 'var(--text-muted)', fontSize: 10.5, textTransform: 'uppercase' }}>
            <th scope="col" style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>Niveau</th>
            <th scope="col" style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>Cible {unite ? `(${unite})` : ''}</th>
            <th scope="col" style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>Écart-type</th>
            <th scope="col" style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>Résultat à seuil</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: nbNiveaux }, (_, i) => i + 1).map((n) => (
            <tr key={n}>
              <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', fontWeight: 700 }}>N{n}</td>
              {seuil(n).actif ? (
                <td colSpan={2} style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span>Résultat attendu</span>
                    <select aria-label={`Sens du critère niveau ${n}`} value={seuil(n).attendu}
                      onChange={(e) => majSeuil(n, { attendu: e.target.value === 'superieur' ? 'superieur' : 'inferieur' })} style={{ ...inputStyle, width: 150 }}>
                      <option value="inferieur">inférieur à</option>
                      <option value="superieur">supérieur ou égal à</option>
                    </select>
                    <input aria-label={`Limite du critère niveau ${n}`} value={seuil(n).limite} placeholder="ex. 7"
                      onChange={(e) => majSeuil(n, { limite: e.target.value })} style={{ ...inputStyle, width: 80 }} />
                    {unite ? <span style={{ color: 'var(--text-muted)' }}>{unite}</span> : null}
                  </div>
                </td>
              ) : (<>
                <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>
                  <input aria-label={`Cible niveau ${n}`} value={valeur(n, 'cible')} onChange={(e) => majCible(n, 'cible', e.target.value)} style={{ ...inputStyle, width: 100 }} />
                </td>
                <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>
                  <input aria-label={`Écart-type niveau ${n}`} value={valeur(n, 'sd')} onChange={(e) => majCible(n, 'sd', e.target.value)} style={{ ...inputStyle, width: 100 }} />
                </td>
              </>)}
              <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>
                <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
                  <input type="checkbox" checked={seuil(n).actif} onChange={(e) => majSeuil(n, { actif: e.target.checked })} />
                  qualitatif
                </label>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
        Valeurs du certificat d’analyse fourni avec le lot de contrôle — une cible et un écart-type par niveau.
        Cochez « qualitatif » pour un contrôle négatif ou à seuil (par exemple « &lt; 7 ») : le résultat se saisit tel
        qu’il s’affiche (« &lt; 2 », « négatif »), il est jugé contre ce critère et ne compte ni dans la moyenne ni dans les règles de Westgard.
      </div>
      {erreur && <div style={{ fontSize: 12.5, color: 'var(--red-600)' }}>{erreur}</div>}
      <PiedActions taille="sm" annuler={{ onClick: () => setOuvert(false) }} principale={{ libelle: 'Enregistrer le lot', libelleChargement: 'Enregistrement…', chargement: estOccupe('lot'), disabled: estOccupe(), onClick: () => void enregistrer() }} />
    </div>
  );
}

/** N1-3 — champs PROPRES d'une carte : l'automate rattaché avant tout, mais
 *  aussi l'unité et le nombre de niveaux. Rien n'était modifiable : une carte
 *  créée sur le mauvais automate à la reprise restait fausse pour toujours.
 *
 *  ⚠️ `nbNiveaux` appartient à la CARTE, pas à l'automate : sur un même appareil
 *  la TSH a 3 niveaux quand l'ACTH n'en a que 2. Le serveur refuse de le réduire
 *  si des passages portent déjà les niveaux supprimés. */
function EditionCarte({ detail, onFini, onErreur, onAnnuler }: {
  detail: Detail;
  onFini: (message: string) => void;
  onErreur: (message: string) => void;
  onAnnuler: () => void;
}) {
  const { equips, fourns } = useGed();
  const [equipement, setEquipement] = useState(detail.carte.equipementId || '');
  const [unite, setUnite] = useState(detail.carte.unite || '');
  /* CQI-3 — le réactif dosé. Porté par la CARTE, pas par le passage : sur un
     système fermé, un analyte sur un automate est dosé par un seul coffret ;
     ce qui change d'un passage à l'autre, c'est le lot. */
  const [fournisseur, setFournisseur] = useState(detail.carte.fournisseurId || '');
  const [reactifDesignation, setReactifDesignation] = useState(detail.carte.reactifDesignation || '');
  const [reactifRef, setReactifRef] = useState(detail.carte.reactifRef || '');
  const [nbNiveaux, setNbNiveaux] = useState(String(detail.carte.nbNiveaux));
  const [libelles, setLibelles] = useState<string[]>(detail.carte.libellesNiveaux || []);
  const { run, estOccupe } = useBusy();

  const automates = useMemo(
    () => [...equips].filter((e) => e.actif !== false).sort((a, b) => (a.code || '').localeCompare(b.code || '')),
    [equips],
  );

  const enregistrer = () => run('carte', async () => {
    const r = await fetchEcriture(`/api/ged/ged/cqi-cartes/${detail.carte.carteId}/champs`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        equipement: equipement || undefined,
        unite,
        fournisseur,
        reactifDesignation,
        reactifRef,
        nbNiveaux: Number(nbNiveaux),
        libellesNiveaux: libelles.slice(0, Number(nbNiveaux)),
      }),
    });
    if (!r.ok) { onErreur((await r.json().catch(() => ({}))).error?.message || 'Modification refusée.'); return; }
    const j = await r.json();
    onFini(j.trace?.length ? `Carte modifiée — ${j.trace.join(' · ')}` : 'Aucun changement.');
  });

  return (
    <div style={{ background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: 12, display: 'grid', gap: 10 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)' }}>
        Modifier la carte {detail.carte.analyteNom}
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 230px' }}>
          <label style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }} htmlFor="ec-eq">Automate rattaché</label>
          <Selecteur id="ec-eq" value={equipement} onChange={setEquipement} style={{ ...inputStyle, minWidth: 220 }}>
            <option value="">— inchangé —</option>
            {automates.map((e) => (
              <option key={e.documentId} value={e.documentId}>{e.code ? `${e.code} · ` : ''}{e.nom}</option>
            ))}
          </Selecteur>
        </div>
        <div style={{ flex: '0 1 120px' }}>
          <label style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }} htmlFor="ec-un">Unité</label>
          <input id="ec-un" value={unite} onChange={(e) => setUnite(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ flex: '0 1 110px' }}>
          <label style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }} htmlFor="ec-nn">Niveaux</label>
          <select id="ec-nn" value={nbNiveaux} onChange={(e) => setNbNiveaux(e.target.value)} style={inputStyle}>
            {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>
      {/* CQI-3 — le RÉACTIF. À ne pas confondre avec le lot de CONTRÔLE, qui
          appartient à la période de lot : relevé en base, les deux colonnes
          n'ont aucune valeur commune, un lot de contrôle couvre plusieurs
          analytes d'un même automate quand un lot de réactif n'en couvre qu'un. */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 230px' }}>
          <label style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }} htmlFor="ec-fo">Fournisseur du réactif</label>
          <Selecteur id="ec-fo" value={fournisseur} onChange={setFournisseur} style={{ ...inputStyle, minWidth: 220 }}>
            <option value="">— aucun —</option>
            {[...fourns].sort((a, b) => (a.nom || '').localeCompare(b.nom || '', 'fr'))
              .map((f) => <option key={f.documentId} value={f.documentId}>{f.nom}</option>)}
          </Selecteur>
        </div>
        <div style={{ flex: '1 1 200px' }}>
          <label style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }} htmlFor="ec-rd">Désignation du réactif</label>
          <input id="ec-rd" value={reactifDesignation} onChange={(e) => setReactifDesignation(e.target.value)}
            placeholder="Elecsys TSH, Indiko Créatinine…" style={inputStyle} />
        </div>
        <div style={{ flex: '0 1 150px' }}>
          <label style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }} htmlFor="ec-rr">Référence catalogue</label>
          <input id="ec-rr" value={reactifRef} onChange={(e) => setReactifRef(e.target.value)}
            style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} />
        </div>
      </div>

      {/* À QUOI SERVENT CES CHAMPS — la question a été posée, elle mérite d'être
          répondue à l'écran plutôt que dans une formation. */}
      <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, maxWidth: '80ch' }}>
        Un <strong>niveau</strong> est un flacon de contrôle : on en passe deux ou trois par série, à des
        concentrations différentes, pour vérifier l’automate en bas, au milieu et en haut de la gamme.
        Ces libellés remplacent « Niveau 1 », « Niveau 2 » partout — sur la courbe, dans le tableau de
        performance et à la saisie. Nommez-les comme le fournisseur les nomme sur les flacons
        (« Normal », « Pathologique », « Niveau A ») : c’est ce nom-là que le technicien lit en main.
      </p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {Array.from({ length: Number(nbNiveaux) }, (_, i) => i).map((i) => (
          <div key={i} style={{ flex: '0 1 150px' }}>
            <label style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 3 }} htmlFor={`ec-lb${i}`}>
              Libellé niveau {i + 1}
            </label>
            <input id={`ec-lb${i}`} value={libelles[i] || ''} placeholder={`Niveau ${i + 1}`}
              onChange={(e) => setLibelles((l) => { const c = [...l]; c[i] = e.target.value; return c; })}
              style={{ ...inputStyle, fontSize: 12.5 }} />
          </div>
        ))}
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
        Toute modification est inscrite au journal chaîné avec l’ancienne valeur — on corrige, on
        ne réécrit pas l’histoire.
      </div>
      <PiedActions taille="sm" annuler={{ onClick: onAnnuler }} principale={{ libelle: 'Enregistrer', libelleChargement: 'Enregistrement…', chargement: estOccupe('carte'), disabled: estOccupe(), onClick: () => void enregistrer() }} />
    </div>
  );
}

/** L'étiquette d'un champ de l'assistant. Hors de `NouvelleCarte` : déclarée
 *  dedans, elle était recréée à chaque frappe et démontait le champ qu'elle
 *  entoure — le curseur sortait de la saisie après chaque caractère. */
function L({ t, children }: { t: string; children: React.ReactNode }) {
  return <label style={{ display: 'grid', gap: 4, fontSize: 12.5, fontWeight: 600 }}><span>{t}</span>{children}</label>;
}

/** Création d'une carte — le couple analyte × automate, unicité gardée côté
 *  moteur. Le reste (règles, période, réactif) se règle ensuite sur la carte. */
/* ─────────────── R11 — ASSISTANT « PREMIÈRE CARTE » ───────────────
 *
 * Quatre étapes, RIEN n'est écrit avant la confirmation finale, et en cas
 * d'échec aucun élément partiel ne subsiste (le serveur retire ce qu'il a
 * créé). Les 126 cartes du parc existent déjà : l'assistant sert à une
 * analyse neuve. Chaque étape dit ce qui lui manque avant de laisser passer. */
function NouvelleCarte({ onCreee }: { onCreee: () => void }) {
  const { showToast } = useGed();
  const { run, estOccupe } = useBusy();
  const [ouvert, setOuvert] = useState(false);
  const [etape, setEtape] = useState(0);
  const [erreur, setErreur] = useState('');
  const [analytes, setAnalytes] = useState<{ documentId: string; code: string; nom: string; uniteDefaut?: string; discipline?: string }[]>([]);
  const [equipements, setEquipements] = useState<{ documentId: string; code: string; nom: string }[]>([]);
  const [techniques, setTechniques] = useState<{ documentId: string; code: string; nom: string }[]>([]);
  const [criteres, setCriteres] = useState<{ documentId: string; libelle: string; source?: { libelle: string; version: string } | null }[] | null>(null);
  const [regles, setRegles] = useState<{ code: string; libelle: string; gravite: string }[]>([]);
  const aujourdhui = aujourdhuiIso();
  const vide = {
    materiau: '', lotControle: '', dateDebut: aujourdhui, datePeremption: '', nbNiveaux: '2', libellesNiveaux: ['Bas', 'Normal', 'Haut'],
    analyte: '', technique: '', equipement: '', unite: '', typeResultat: 'quantitatif' as 'quantitatif' | 'qualitatif',
    sourceCible: 'fournisseur' as 'fournisseur' | 'interne', cibles: [{ cible: '', sd: '', attendu: 'inferieur', limite: '' }, { cible: '', sd: '', attendu: 'inferieur', limite: '' }, { cible: '', sd: '', attendu: 'inferieur', limite: '' }],
    regles: [] as string[], teaSource: 'aucune' as 'aucune' | 'carte' | 'referentiel', teaValeur: '', teaReference: '', teaCritereRef: '', teaColonne: 'inexactitude', teaNiveauSfbc: {} as Record<string, string>,
  };
  const [f, setF] = useState(vide);
  const set = (k: keyof typeof vide, v: unknown) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    if (!ouvert || analytes.length) return;
    void fetch('/api/ged/ged-analytes?sort=code:asc&pagination[pageSize]=200', { credentials: 'include' }).then((r) => (r.ok ? r.json() : null)).then((j) => { if (j?.data) setAnalytes(j.data); });
    void fetch('/api/ged/ged-equipements?sort=code:asc&pagination[pageSize]=100&filters[categorie][$null]=false', { credentials: 'include' }).then((r) => (r.ok ? r.json() : null)).then((j) => { if (j?.data) setEquipements(j.data); });
    void fetch('/api/ged/techniques?sort=code:asc&pagination[pageSize]=200', { credentials: 'include' }).then((r) => (r.ok ? r.json() : null)).then((j) => { if (j?.data) setTechniques(j.data); }).catch(() => undefined);
    void fetch('/api/ged/ged-regle-cqis?sort=code:asc&pagination[pageSize]=50', { credentials: 'include' }).then((r) => (r.ok ? r.json() : null)).then((j) => {
      if (j?.data) { setRegles(j.data); setF((x) => ({ ...x, regles: j.data.filter((r: { actif?: boolean }) => r.actif !== false).map((r: { code: string }) => r.code) })); }
    });
  }, [ouvert, analytes.length]);
  const a = analytes.find((x) => x.documentId === f.analyte);
  // Autre paramètre, ou retour au référentiel : « Lecture du référentiel… »
  // pendant la requête, recalé au rendu plutôt que dans l'effet.
  const cleCriteres = `${a?.code || ''}|${f.teaSource}`;
  const [criteresVus, setCriteresVus] = useState(cleCriteres);
  if (criteresVus !== cleCriteres) {
    setCriteresVus(cleCriteres);
    if (a?.code && f.teaSource === 'referentiel') setCriteres(null);
  }
  useEffect(() => {
    if (!a?.code || f.teaSource !== 'referentiel') return;
    // Le référentiel est servi par le moteur (`GET /ged/ref-criteres`), pas par le CRUD REST : même porte que l'écran des critères.
    void fetch('/api/ged/ged/ref-criteres', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { lignes?: { documentId: string; libelle: string; analyteCode: string | null; sourceCode: string | null; sourceVersion: string | null }[] } | null) => setCriteres(
        (j?.lignes || []).filter((l) => String(l.analyteCode || '').toLowerCase() === a.code.toLowerCase())
          .map((l) => ({ documentId: l.documentId, libelle: l.libelle, source: l.sourceCode ? { libelle: l.sourceCode, version: l.sourceVersion || '' } : null })),
      ))
      .catch(() => setCriteres([]));
  }, [a?.code, f.teaSource]);

  const nb = Math.max(1, Math.min(3, parseInt(f.nbNiveaux, 10) || 2));
  /** Ce qui manque à chaque étape — dit, jamais tu. */
  const manques: string[][] = [
    [
      ...(f.lotControle.trim() ? [] : ['numéro de lot de contrôle']),
      ...(f.dateDebut ? [] : ['date d’ouverture']),
      ...(f.dateDebut && f.dateDebut > aujourdhui ? ['ouverture non future'] : []),
      ...(f.datePeremption && f.datePeremption < aujourdhui ? ['péremption non passée'] : []),
      ...(f.datePeremption && f.dateDebut && f.datePeremption < f.dateDebut ? ['péremption après ouverture'] : []),
    ],
    [...(f.analyte ? [] : ['paramètre']), ...(f.equipement ? [] : ['automate'])],
    [
      ...Array.from({ length: nb }, (_, i) => i).flatMap((i) => {
        const c = f.cibles[i]!;
        if (f.typeResultat === 'qualitatif') return Number.isFinite(Number(String(c.limite).replace(',', '.'))) && c.limite !== '' ? [] : [`niveau ${i + 1} : limite du critère`];
        return (Number(String(c.cible).replace(',', '.')) || c.cible === '0') && Number(String(c.sd).replace(',', '.')) > 0 ? [] : [`niveau ${i + 1} : cible et écart-type fournisseur (> 0)`];
      }),
      ...(f.regles.length ? [] : ['au moins une règle active']),
      ...(f.teaSource === 'carte' && !(Number(String(f.teaValeur).replace(',', '.')) > 0 && f.teaReference.trim().length >= 3) ? ['TEa : valeur > 0 et référence'] : []),
      ...(f.teaSource === 'referentiel' && !f.teaCritereRef ? ['TEa : critère du référentiel'] : []),
      ...(f.teaSource === 'referentiel' && f.teaCritereRef && Array.from({ length: nb }, (_, i) => i + 1).some((n) => !f.teaNiveauSfbc[String(n)]) ? ['TEa : rattachement Bas/Moyen/Élevé de chaque niveau'] : []),
    ],
    [],
  ];
  const corps = () => ({
    lotControle: f.lotControle, dateDebut: f.dateDebut, datePeremption: f.datePeremption || undefined, marqueControle: f.materiau || undefined,
    nbNiveaux: nb, libellesNiveaux: f.libellesNiveaux.slice(0, nb),
    analyte: f.analyte, technique: f.technique || undefined, equipement: f.equipement, unite: f.unite || a?.uniteDefaut || '', discipline: a?.discipline || '', typeResultat: f.typeResultat,
    sourceCible: f.sourceCible, cibles: f.cibles.slice(0, nb).map((c, i) => ({ niveau: i + 1, ...c })), regles: f.regles,
    teaSource: f.teaSource, teaValeur: f.teaValeur || undefined, teaReference: f.teaReference || undefined, teaCritereRef: f.teaCritereRef || undefined, teaColonne: f.teaColonne, teaNiveauSfbc: f.teaNiveauSfbc,
  });
  const envoyer = (confirme: boolean) => run('assistant', async () => {
    setErreur('');
    const r = await fetchEcriture('/api/ged/ged/cqi-cartes/assistant', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...corps(), confirme }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { setErreur(j.error?.message || 'Refusé.'); return; }
    if (confirme) {
      showToast('Carte créée avec son lot — rien d’autre n’est à régler pour saisir.', 'info');
      setOuvert(false); setEtape(0); setF(vide); onCreee();
    } else setEtape(3);
  });

  if (!ouvert) {
    return (
      <Bouton variante="primaire" taille="sm" onClick={() => setOuvert(true)} title="Assistant en 4 étapes : matériau et lot, paramètre et automate, cible et règles, récapitulatif. Rien n’est écrit avant la confirmation.">
        <I n="plus" s={13} />Nouvelle carte
      </Bouton>
    );
  }
  const ETAPES = ['Matériau et lot', 'Paramètre et automate', 'Cible, règles, TEa', 'Récapitulatif'];
  const champ = { ...inputStyle, fontSize: 12.5 };
  return (
    <Modale titre="Nouvelle carte de contrôle — assistant" onClose={() => setOuvert(false)} largeur={760}
      sousTitre="Quatre étapes ; rien n’est écrit avant « Confirmer et créer », et un échec ne laisse rien derrière lui."
      pied={(
        <>
          <Bouton taille="sm" onClick={() => setOuvert(false)}>Annuler</Bouton>
          {etape > 0 && <Bouton taille="sm" onClick={() => setEtape((e) => e - 1)}>Précédent</Bouton>}
          {etape < 2 && <Bouton variante="primaire" taille="sm" onClick={() => setEtape((e) => e + 1)} disabled={manques[etape]!.length > 0} title={manques[etape]!.length ? `Il manque : ${manques[etape]!.join(', ')}` : 'Étape suivante'}>Suivant</Bouton>}
          {etape === 2 && <Bouton variante="primaire" taille="sm" onClick={() => envoyer(false)} disabled={estOccupe() || manques[2]!.length > 0} title={manques[2]!.length ? `Il manque : ${manques[2]!.join(', ')}` : 'Le serveur vérifie tout et montre le récapitulatif — rien n’est écrit'}>Vérifier</Bouton>}
          {etape === 3 && <Bouton variante="primaire" taille="sm" onClick={() => envoyer(true)} disabled={estOccupe()} title="Crée la carte et son lot en un seul geste">{estOccupe() ? 'Création…' : 'Confirmer et créer'}</Bouton>}
        </>
      )}>
      <div style={{ display: 'grid', gap: 12 }}>
        {/* Le composant commun des frises (lot D) : on peut revenir à une étape franchie. */}
        <Frise etapes={ETAPES.map((t, i) => ({ k: String(i), label: t }))} courante={String(etape)} label="Étapes de l’assistant"
          onChange={(k) => { if (Number(k) < etape) setEtape(Number(k)); }} />
        {erreur && <div style={{ fontSize: 12.5, color: 'var(--red-600)', whiteSpace: 'pre-line' }}>{erreur}</div>}
        {manques[etape]!.length > 0 && <div style={{ fontSize: 12, color: 'var(--amber-600)' }}>Il manque : {manques[etape]!.join(' · ')}</div>}

        {etape === 0 && (
          <div style={{ display: 'grid', gap: 10 }}>
            <L t="Matériau de contrôle (marque, produit)"><input value={f.materiau} onChange={(e) => set('materiau', e.target.value)} style={champ} placeholder="Ex. : Bio-Rad Lyphochek Immunoassay Plus" /></L>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <L t="Numéro de lot *"><input value={f.lotControle} onChange={(e) => set('lotControle', e.target.value)} style={champ} /></L>
              <L t="Ouverture *"><input type="date" value={f.dateDebut} max={aujourdhui} onChange={(e) => set('dateDebut', e.target.value)} style={champ} /></L>
              <L t="Péremption"><input type="date" value={f.datePeremption} min={aujourdhui} onChange={(e) => set('datePeremption', e.target.value)} style={champ} /></L>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 10 }}>
              <L t="Niveaux"><select value={f.nbNiveaux} onChange={(e) => set('nbNiveaux', e.target.value)} style={champ}>{['1', '2', '3'].map((n) => <option key={n} value={n}>{n}</option>)}</select></L>
              <L t="Libellés des niveaux"><div style={{ display: 'flex', gap: 6 }}>{Array.from({ length: nb }, (_, i) => <input key={i} value={f.libellesNiveaux[i] || ''} onChange={(e) => set('libellesNiveaux', f.libellesNiveaux.map((l, k) => (k === i ? e.target.value : l)))} style={{ ...champ, width: 110 }} />)}</div></L>
            </div>
          </div>
        )}
        {etape === 1 && (
          <div style={{ display: 'grid', gap: 10 }}>
            <L t="Paramètre *"><Selecteur value={f.analyte} onChange={(v) => { const x = analytes.find((y) => y.documentId === v); setF((s) => ({ ...s, analyte: v, unite: s.unite || x?.uniteDefaut || '' })); }} aria-label="Paramètre"><option value="">—</option>{analytes.map((x) => <option key={x.documentId} value={x.documentId}>{x.code} — {x.nom}</option>)}</Selecteur></L>
            <L t="Automate *"><Selecteur value={f.equipement} onChange={(v) => set('equipement', v)} aria-label="Automate"><option value="">—</option>{equipements.map((e) => <option key={e.documentId} value={e.documentId}>{e.code} — {e.nom}</option>)}</Selecteur></L>
            <L t="Technique"><Selecteur value={f.technique} onChange={(v) => set('technique', v)} aria-label="Technique"><option value="">— (à rattacher plus tard si besoin)</option>{techniques.map((t) => <option key={t.documentId} value={t.documentId}>{t.code} — {t.nom}</option>)}</Selecteur></L>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <L t="Unité"><input value={f.unite} onChange={(e) => set('unite', e.target.value)} style={champ} placeholder="reprise du paramètre si vide" /></L>
              <L t="Type de résultat"><select value={f.typeResultat} onChange={(e) => set('typeResultat', e.target.value)} style={champ}><option value="quantitatif">Quantitatif (cible ± σ)</option><option value="qualitatif">Qualitatif (critère d’acceptation)</option></select></L>
            </div>
          </div>
        )}
        {etape === 2 && (
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'grid', gap: 6 }}>
              <strong style={{ fontSize: 12.5 }}>Cible{f.typeResultat === 'qualitatif' ? ' — critère d’acceptation par niveau' : ''}</strong>
              {f.typeResultat === 'quantitatif' && (
                <div style={{ display: 'flex', gap: 14, fontSize: 12.5 }}>
                  <label style={{ display: 'flex', gap: 5, alignItems: 'center' }}><input type="radio" checked={f.sourceCible === 'fournisseur'} onChange={() => set('sourceCible', 'fournisseur')} />Fournisseur (certificat)</label>
                  <label style={{ display: 'flex', gap: 5, alignItems: 'center' }} title="La carte démarre en baseline : aucune règle évaluée jusqu’à l’approbation de la cible interne (R1). Les valeurs fournisseur servent de repli."><input type="radio" checked={f.sourceCible === 'interne'} onChange={() => set('sourceCible', 'interne')} />Interne à constituer (baseline)</label>
                </div>
              )}
              {Array.from({ length: nb }, (_, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5 }}>
                  <span style={{ minWidth: 90, fontWeight: 600 }}>{f.libellesNiveaux[i] || `Niveau ${i + 1}`}</span>
                  {f.typeResultat === 'qualitatif' ? (<>
                    <select value={f.cibles[i]!.attendu} onChange={(e) => set('cibles', f.cibles.map((c, k) => (k === i ? { ...c, attendu: e.target.value } : c)))} style={{ ...champ, width: 150 }}><option value="inferieur">inférieur à</option><option value="superieur">supérieur ou égal à</option></select>
                    <input value={f.cibles[i]!.limite} onChange={(e) => set('cibles', f.cibles.map((c, k) => (k === i ? { ...c, limite: e.target.value } : c)))} style={{ ...champ, width: 90 }} placeholder="limite" />
                  </>) : (<>
                    <input value={f.cibles[i]!.cible} onChange={(e) => set('cibles', f.cibles.map((c, k) => (k === i ? { ...c, cible: e.target.value } : c)))} style={{ ...champ, width: 110 }} placeholder="cible" inputMode="decimal" />
                    <span>±</span>
                    <input value={f.cibles[i]!.sd} onChange={(e) => set('cibles', f.cibles.map((c, k) => (k === i ? { ...c, sd: e.target.value } : c)))} style={{ ...champ, width: 90 }} placeholder="σ" inputMode="decimal" />
                    <span style={{ color: 'var(--text-muted)' }}>{f.unite || a?.uniteDefaut || ''}</span>
                  </>)}
                </div>
              ))}
            </div>
            <div style={{ display: 'grid', gap: 6 }}>
              <strong style={{ fontSize: 12.5 }}>Profil de règles</strong>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 12.5 }}>
                {regles.map((r) => (
                  <label key={r.code} style={{ display: 'flex', gap: 5, alignItems: 'center' }} title={r.libelle}>
                    <input type="checkbox" checked={f.regles.includes(r.code)} onChange={(e) => set('regles', e.target.checked ? [...f.regles, r.code] : f.regles.filter((c) => c !== r.code))} />
                    <span style={{ fontFamily: 'var(--font-mono)' }}>{r.code}</span><span style={{ color: r.gravite === 'rejet' ? 'var(--red-600)' : 'var(--amber-600)', fontSize: 10.5 }}>{r.gravite}</span>
                  </label>
                ))}
              </div>
            </div>
            <div style={{ display: 'grid', gap: 6 }}>
              <strong style={{ fontSize: 12.5 }}>TEa et sa source (les trois possibilités, vous tranchez)</strong>
              <div style={{ display: 'flex', gap: 14, fontSize: 12.5, flexWrap: 'wrap' }}>
                {([['aucune', 'Aucune — performance non calculable, dit'], ['carte', 'Propre à la carte'], ['referentiel', 'Référentiel']] as const).map(([k, l]) => (
                  <label key={k} style={{ display: 'flex', gap: 5, alignItems: 'center' }}><input type="radio" checked={f.teaSource === k} onChange={() => set('teaSource', k)} />{l}</label>
                ))}
              </div>
              {f.teaSource === 'carte' && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <input value={f.teaValeur} onChange={(e) => set('teaValeur', e.target.value)} style={{ ...champ, width: 100 }} placeholder="TEa %" inputMode="decimal" />
                  <input value={f.teaReference} onChange={(e) => set('teaReference', e.target.value)} style={{ ...champ, flex: 1 }} placeholder="Référence bibliographique (obligatoire)" />
                </div>
              )}
              {f.teaSource === 'referentiel' && (
                <div style={{ display: 'grid', gap: 6 }}>
                  {criteres === null ? <span style={{ fontSize: 12 }}>Lecture du référentiel…</span> : !criteres.length ? (
                    <span style={{ fontSize: 12, color: 'var(--amber-600)' }}>Aucun critère du référentiel ne vise {a?.code || 'ce paramètre'} : choisissez « aucune » ou « propre à la carte », ou créez le critère d’abord.</span>
                  ) : (<>
                    <select value={f.teaCritereRef} onChange={(e) => set('teaCritereRef', e.target.value)} style={champ}><option value="">— critère —</option>{criteres.map((k) => <option key={k.documentId} value={k.documentId}>{k.libelle}{k.source ? ` — ${k.source.libelle} ${k.source.version}` : ''}</option>)}</select>
                    <select value={f.teaColonne} onChange={(e) => set('teaColonne', e.target.value)} style={champ}>
                      {[['inexactitude', 'Inexactitude (erreur totale)'], ['interpretation', 'Normes d’interprétation'], ['justesse', 'Justesse'], ['reproductibilite', 'Reproductibilité'], ['repetabilite', 'Répétabilité'], ['suivi', 'Normes de suivi']].map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                    {Array.from({ length: nb }, (_, i) => i + 1).map((n) => (
                      <label key={n} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5 }}><span style={{ minWidth: 120 }}>{f.libellesNiveaux[n - 1] || `Niveau ${n}`} →</span>
                        <select value={f.teaNiveauSfbc[String(n)] || ''} onChange={(e) => set('teaNiveauSfbc', { ...f.teaNiveauSfbc, [String(n)]: e.target.value })} style={{ ...champ, width: 140 }}><option value="">— choisir —</option><option value="bas">Bas</option><option value="moyen">Moyen</option><option value="eleve">Élevé</option></select>
                      </label>
                    ))}
                  </>)}
                </div>
              )}
            </div>
          </div>
        )}
        {etape === 3 && (
          <div style={{ display: 'grid', gap: 6, fontSize: 12.5 }}>
            <strong>Récapitulatif — vérifié par le serveur, rien n’est encore écrit</strong>
            <span>Lot <strong>{f.lotControle}</strong>{f.materiau ? ` (${f.materiau})` : ''}, ouvert le {fmtDate(f.dateDebut)}{f.datePeremption ? `, péremption ${fmtDate(f.datePeremption)}` : ', péremption non renseignée (à compléter sur le lot)'} · {nb} niveau(x) : {f.libellesNiveaux.slice(0, nb).join(', ')}</span>
            <span>Paramètre <strong>{a?.code} — {a?.nom}</strong> sur <strong>{equipements.find((e) => e.documentId === f.equipement)?.code}</strong>{f.technique ? ` · technique ${techniques.find((t) => t.documentId === f.technique)?.code}` : ''} · unité {f.unite || a?.uniteDefaut || '—'} · {f.typeResultat}</span>
            <span>Cible : {f.typeResultat === 'qualitatif' ? 'critères d’acceptation' : f.sourceCible === 'interne' ? 'INTERNE à constituer — la carte démarre en baseline, aucune règle évaluée jusqu’à l’approbation' : 'fournisseur'} · {f.cibles.slice(0, nb).map((c, i) => `${f.libellesNiveaux[i] || `N${i + 1}`} ${f.typeResultat === 'qualitatif' ? `${c.attendu === 'superieur' ? '≥' : '<'} ${c.limite}` : `${c.cible} ± ${c.sd}`}`).join(' · ')}</span>
            <span>Règles : {f.regles.join(', ')} · TEa : {f.teaSource === 'aucune' ? 'aucune (performance non calculable)' : f.teaSource === 'carte' ? `${f.teaValeur} % — ${f.teaReference}` : `référentiel, colonne ${f.teaColonne}`}</span>
          </div>
        )}
      </div>
    </Modale>
  );
}

/** « Archiver cette carte » (RQ, 03/10/2026). Pour un NOUVEAU LOT, il n'y a rien à faire ici : « Nouveau lot de
 *  contrôle » archive l'ancien. Ce geste-ci est l'autre cas : la carte ne sert plus. Le motif part au journal. */
function ArchiverCarteCqi({ carteId, aUnLotOuvert, onFait }: { carteId: string; aUnLotOuvert: boolean; onFait: () => void }) {
  const [ouvert, setOuvert] = useState(false);
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();
  if (!ouvert) {
    return (
      <Bouton taille="sm" onClick={() => setOuvert(true)} title="La carte quitte les cartes en cours ; son lot en cours est refermé">
        <I n="archive" s={13} />Archiver cette carte
      </Bouton>
    );
  }
  return (
    <div style={{ display: 'grid', gap: 8, padding: 12, border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', background: 'var(--surface-sunken)' }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Archiver la carte</div>
      <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-muted)' }}>
        {aUnLotOuvert ? 'Le lot en cours est refermé et ' : ''}la carte sort des cartes en cours. Rien n’est effacé.
        Pour un <strong>nouveau lot</strong>, utilisez plutôt « Nouveau lot de contrôle » : l’ancien lot se referme tout seul et reste consultable dans la carte.
      </p>
      <input value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Pourquoi ? (obligatoire)" aria-label="Motif de l’archivage" style={inputStyle} />
      {erreur && <div role="alert" style={{ fontSize: 12.5, color: 'var(--red-600)' }}>{erreur}</div>}
      <PiedActions taille="sm" annuler={{ onClick: () => { setOuvert(false); setErreur(''); } }} principale={{ libelle: 'Archiver', libelleChargement: 'Archivage…', chargement: estOccupe('archiver'), disabled: motif.trim().length < 5 || estOccupe(), onClick: () => void run('archiver', async () => {
            const r = await fetchEcriture(`/api/ged/ged/cqi-cartes/${carteId}/archiver`, {
              method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ motif: motif.trim() }),
            });
            if (!r.ok) { setErreur((await r.json().catch(() => ({}))).error?.message || 'Archivage refusé.'); return; }
            onFait();
          }) }} />
    </div>
  );
}

export function Cqi() {
  const { demanderTexte, confirmation } = useConfirmation();
  const {
    session, settings, openSettings, selectedCqiCarte,
    cartesCqi, recapCqi, refresh, loading, booted, backendError, naviguerVers,
  } = useGed();
  const peutParametrer = peut(settings?.perms, session.profile, 'cqiParametrer');
  /* R1/R10 — approuver (figer) une cible interne : droit neuf, repli sur
     `cqiValider` quand le paramétrage ne porte pas la clé. Même règle que le
     serveur (`peutDroitOuRepli`) : l'écran masque, le serveur refuse. */
  const peutApprouverCible = peutOuRepli(settings?.perms, session.profile, 'cqiApprouverCible', 'cqiValider');
  /* Le renvoi vers le paramétrage n'a de sens que pour qui peut y entrer :
     l'annoncer à quelqu'un dont le bouton ouvrira une fenêtre sans cette
     rubrique serait une fausse piste. Même droit que la rubrique elle-même. */
  const peutReglerCqi = peut(settings?.perms, session.profile, 'cqiRegles');

  /* Plus d'état local : `resumes` et `recap` sont des VUES du magasin.
     Le récapitulatif est SERVI calculé (N1-2) : le recompter ici en ferait un
     second calcul, donc une seconde vérité possible. */
  const resumes = cartesCqi as Resume[];
  const recap: Recap | null = recapCqi;
  const attend = enChargement(booted, loading, resumes.length);
  const [filtreEquip, setFiltreEquip] = useState('');
  const [filtreEtat, setFiltreEtat] = useState('');
  const [filtreFourn, setFiltreFourn] = useState('');
  const [q, setQ] = useState('');
  const [ouverte, setOuverte] = useState<string | null>(null);
  /* C8 — arrivée ADRESSÉE : « EQUIPEMENT:ANALYTE » venu de la saisie (ou d'une
     URL partagée) s'ouvre dès que les résumés sont là. La référence est
     résolue ici, jamais transportée en documentId : l'adresse survit ainsi à
     une reprise de base. */
  const refDemandee = React.useRef<string | null>(null);
  useEffect(() => {
    if (!selectedCqiCarte || !resumes.length) return;
    if (refDemandee.current === selectedCqiCarte) return;
    refDemandee.current = selectedCqiCarte;
    const [equip, analyte] = selectedCqiCarte.split(':');
    const hit = resumes.find((r) => r.equipement === equip && r.analyte === analyte);
    /* Une référence qui ne résout pas ne casse rien : l'écran s'ouvre sur la
       liste, où la recherche retrouvera la carte — mieux qu'une erreur sèche
       sur une carte renommée. */
    /* ⚠️ FAUX POSITIF ASSUMÉ (02/09/2026). Ce n'est pas un état ajusté à une
       entrée : c'est la CONSOMMATION d'une demande arrivée de l'extérieur — un
       lien profond posé par un autre écran. Un effet est le bon endroit pour
       réagir à un système externe, et la demande est remise à `null` juste
       après pour ne pas se rejouer. La dériver n'a pas de sens : elle n'est pas
       une fonction de l'état, elle est un événement. */
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (hit) setOuverte(hit.carteId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCqiCarte, resumes]);
  const [detail, setDetail] = useState<Detail | null>(null);
  // Sélecteur de fenêtre temporelle (J4) : « le lot en cours » reste le mode
  // par défaut (ancien binaire lot/périodes), les autres valeurs bornent le
  // mode « tout l'historique » à une fenêtre glissante ou personnalisée —
  // souvent la bonne unité d'analyse n'est pas le calendrier civil.
  const [fenetre, setFenetre] = useState<'lot' | '7j' | '30j' | '90j' | '12mois' | 'tout' | 'perso' | 'archive'>('lot');
  /* ⚠️ LA PÉRIODE ARCHIVÉE OUVERTE (12/09/2026). « Où sont passées les
     anciennes cartes de CQI des autres lots de contrôle sur les Indiko ? Je ne
     vois pas de bouton Cartes archivées. » — Manel, 11/09.
     Rien n'était perdu : 48 périodes clôturées sur les deux Indiko, 8 et 9 lots
     distincts. Le MOTEUR sait déjà les servir une par une (`?mode=lot&periode=`).
     Il n'existait simplement aucune porte à l'écran : le sélecteur proposait
     « le lot en cours » ou des fenêtres de temps qui FONDENT tous les lots dans
     une seule courbe — ce qui n'est pas « voir la carte du lot précédent ».
     `''` = le lot en cours, comme avant. */
  const [periodeOuverte, setPeriodeOuverte] = useState('');
  const [persoDepuis, setPersoDepuis] = useState('');
  const [persoJusqua, setPersoJusqua] = useState('');
  const [selection, setSelection] = useState<Point | null>(null);
  /* Quels niveaux tracer, et sur quelle fenêtre de temps (RQ, 18/08). `null` =
     tous les niveaux : c'est la vue de routine, et l'exiger de cocher trois
     cases à chaque ouverture ferait perdre du temps à chaque consultation. */
  const [niveauxVus, setNiveauxVus] = useState<number[] | null>(null);
  const [zoom, setZoom] = useState<{ debut: number; fin: number }>({ debut: 0, fin: 1 });
  /** Calibration ouverte DEPUIS un point déviant — elle en portera la série et
   *  la règle enfreinte, ce qui la distingue d'une calibration de routine. */
  const [calibrerDepuis, setCalibrerDepuis] = useState<{
    equipementCode: string; analyteLibelle: string; analyteCode: string; serie: string; regle: string;
  } | null>(null);
  const [message, setMessage] = useState('');
  const [erreur, setErreur] = useState('');
  /** Règles en cours de modification, pas encore enregistrées (N1-6).
   *  `null` = aucune modification en attente. */
  const [brouillonRegles, setBrouillonRegles] = useState<string[] | null>(null);
  const [commentaire, setCommentaire] = useState('');
  const [motifDerogation, setMotifDerogation] = useState('');
  const [motifAnnulation, setMotifAnnulation] = useState('');
  const [annulerOuvert, setAnnulerOuvert] = useState(false);
  const [editionCarte, setEditionCarte] = useState(false);
  const { run, estOccupe } = useBusy();
  const estRq = session.roles.includes('rq');
  // Non dérogeable sauf pour qui porte déjà ce droit — ou pour un responsable
  // qui s'authentifie pour CE geste depuis le poste du technicien, sans ouvrir
  // de session (06/09/2026, `autoriserResponsable` côté serveur).
  const peutDeroger = estBiologiste(session.profile, settings?.perms);
  const authDerogation = useAutorisationResponsable();
  const authAnnulation = useAutorisationResponsable();

  /* ── BLOQ-01 : L'ÉCRAN LIT LE MAGASIN, IL NE RECHARGE PLUS POUR SON COMPTE
     ─────────────────────────────────────────────────────────────────────────
     Relevé du 26/08/2026 : l'écran affichait « Aucune carte de contrôle ·
     0 carte(s) · 0 point(s) » pendant que la barre latérale annonçait 41 cartes
     en défaut et que l'indicateur comptait 1 526 passages sur 2 030. Les deux
     lisaient LA MÊME route.

     La cause n'était pas la donnée, elle était la forme de l'appel : un `fetch`
     propre à l'écran, tiré UNE FOIS au montage, et terminé par `if (!r.ok)
     return;`. Trois défauts en une ligne —
       · il échoue en SILENCE : pas de message, pas de témoin, rien à l'écran ;
       · l'état vide qui reste est CRÉDIBLE (« aucune carte »), donc on le croit ;
       · il ne se rejoue JAMAIS, alors que le magasin, lui, se recharge après
         chaque écriture. D'où le désaccord durable entre la barre et l'écran.

     Le magasin est désormais la seule source : il porte les cartes ET le
     récapitulatif servi calculé, il se rejoue à chaque `refresh()`, et son
     échec est déjà signalé par `backendError`. */
  const chargerResumes = useCallback(async () => { await refresh(); }, [refresh]);

  // Bornes dérivées de la fenêtre choisie — calculées une fois par rendu, pas
  // à chaque frappe : `new Date()` en tête d'effet romprait `set-state-in-effect`.
  const bornes = useMemo(() => {
    if (fenetre === 'lot' || fenetre === 'tout' || fenetre === 'archive') return { depuis: '', jusqua: '' };
    if (fenetre === 'perso') return { depuis: persoDepuis, jusqua: persoJusqua };
    const jours = fenetre === '7j' ? 7 : fenetre === '30j' ? 30 : fenetre === '90j' ? 90 : 365;
    const d = new Date();
    d.setDate(d.getDate() - jours);
    return { depuis: isoJour(d), jusqua: '' };
  }, [fenetre, persoDepuis, persoJusqua]);

  const chargerDetail = useCallback(async (carteId: string, m: 'lot' | 'periode', depuis: string, jusqua: string, periode: string) => {
    const qs = new URLSearchParams({ mode: m });
    if (depuis) qs.set('depuis', depuis);
    if (jusqua) qs.set('jusqua', jusqua);
    /* La période demandée ne vaut qu'en mode `lot` : c'est ce qui rend la carte
       d'un lot ARCHIVÉ, seule, avec la cible et les limites qui étaient les
       siennes. En mode `periode`, le serveur les rend toutes. */
    if (periode && m === 'lot') qs.set('periode', periode);
    const r = await fetch(`/api/ged/ged/cqi-cartes/${carteId}?${qs}`, { credentials: 'include' });
    setDetail(r.ok ? await r.json() : null);
    setSelection(null);
    // Un rechargement abandonne le brouillon de règles : le laisser en place
    // ferait cohabiter des cases cochées avec un état serveur différent.
    setBrouillonRegles(null);
    setEditionCarte(false);
  }, []);

  /** La sélection d'un point emporte l'état de son formulaire de suite. Fait
   *  ICI, au geste, et non dans un effet : `set-state-in-effect` est une erreur
   *  de lint dans ce module, et à juste titre — l'effet rejouerait à contretemps. */
  const choisirPoint = useCallback((p: Point | null) => {
    setSelection(p);
    setCommentaire(p?.commentaire || '');
    setMotifDerogation(p?.motifDerogation || '');
    setAnnulerOuvert(false);
    setMotifAnnulation('');
  }, []);

  /* « archive » est un mode `lot` : on regarde UN lot, simplement pas celui en
     cours. C'est ce qui lui rend ses propres limites — une fenêtre de temps les
     recalculerait sur l'ensemble. */
  const mode: 'lot' | 'periode' = (fenetre === 'lot' || fenetre === 'archive') ? 'lot' : 'periode';
  const periodeDemandee = fenetre === 'archive' ? periodeOuverte : '';
  /* Les lots CLÔTURÉS de la carte ouverte, du plus récent au plus ancien : on
     cherche presque toujours celui d'avant.
     ⚠️ `detail.periodes` les porte TOUS, quelle que soit la fenêtre — c'est le
     serveur qui les liste, et il ne filtre pas cette liste-là. On peut donc
     proposer l'archive sans recharger quoi que ce soit.
     ⚠️ Une période clôturée SANS AUCUN POINT n'est pas une carte à retrouver :
     un lot ouvert puis abandonné. La proposer enverrait chercher une courbe
     vide. */
  const archivees = useMemo(
    () => (detail?.periodes || [])
      .filter((p) => p.cloturee && (p.points === null || p.points > 0))
      .sort((a, b) => String(b.dateDebut || '').localeCompare(String(a.dateDebut || ''))),
    [detail],
  );

  // `setDetail(null)` ne doit pas partir en tête d'effet : la fermeture est un
  // geste de l'utilisateur, elle se traite là où elle est déclenchée. Ici on ne
  // fait que charger, et le drapeau évite d'écrire après un changement de carte.
  useEffect(() => {
    if (!ouverte) return undefined;
    let vivant = true;
    void Promise.resolve().then(() => (vivant ? chargerDetail(ouverte, mode, bornes.depuis, bornes.jusqua, periodeDemandee) : undefined));
    return () => { vivant = false; };
  }, [ouverte, mode, bornes.depuis, bornes.jusqua, periodeDemandee, chargerDetail]);

  const equipements = useMemo(
    () => [...new Set(resumes.map((r) => `${r.equipement}|${r.equipementNom}`))].sort(),
    [resumes],
  );
  /* Les fournisseurs RÉELLEMENT portés par des cartes, pas le registre entier :
     114 fournisseurs dans un menu dont trois servent ici en ferait une liste
     qu'on n'ouvre plus. */
  const fournisseurs = useMemo(
    () => [...new Set(resumes.map((r) => r.fournisseur).filter((x): x is string => !!x))].sort((a, b) => a.localeCompare(b, 'fr')),
    [resumes],
  );
  // Les deux menus déroulants suffisaient quand une carte se trouvait « par
  // automate ». Sur un parc réel, on cherche un ANALYTE — « glycémie », « TSH » —
  // sans savoir sur quel appareil il tourne : c'est la recherche qui répond,
  // pas le filtre.
  const liste = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return resumes.filter((r) => {
      if (filtreEquip && r.equipement !== filtreEquip) return false;
      if (filtreEtat === 'aparametrer' && !r.aParametrer) return false;
      if (filtreEtat && filtreEtat !== 'aparametrer' && r.statut !== filtreEtat) return false;
      if (filtreFourn && r.fournisseur !== filtreFourn) return false;
      // CQI-4 — la recherche porte AUSSI sur le fournisseur, le réactif et les
      // deux lots. Sans eux, la question posée en cas de rappel de lot — « quelles
      // cartes tournent sur ce coffret ? » — n'avait aucune réponse dans l'outil :
      // il fallait rouvrir le classeur, c'est-à-dire ne jamais le faire.
      if (qq && !`${r.analyte} ${r.analyteNom} ${r.matiere || ''} ${r.equipementNom} ${r.equipement} ${r.discipline} ${r.fournisseur || ''} ${r.reactifDesignation || ''} ${r.reactifRef || ''} ${r.lotReactif || ''} ${r.lotControle || ''}`.toLowerCase().includes(qq)) return false;
      return true;
    });
  }, [resumes, filtreEquip, filtreEtat, filtreFourn, q]);


  /* ── OUVRIR EN FENÊTRE, ET PARCOURIR (retour Ines du 17/08) ───────────────
   *
   * La carte s'ouvrait en accordéon sous sa ligne : pour comparer deux cartes
   * il fallait replier l'une, retrouver l'autre dans une liste de 126, et le
   * graphe repartait chaque fois du haut de page. Une revue de CQI se fait
   * carte après carte — c'est le geste, et rien ne le portait.
   *
   * Le rang se calcule sur `liste`, c'est-à-dire sur ce qui est RÉELLEMENT
   * affiché : filtré et recherché. Naviguer sur `resumes` ferait sortir du
   * filtre qu'on vient de poser — on chercherait « les rejets », et « suivant »
   * emmènerait sur une carte conforme.
   */
  const rang = ouverte ? liste.findIndex((r) => r.carteId === ouverte) : -1;
  const precedente = rang > 0 ? liste[rang - 1] : null;
  const suivante = rang >= 0 && rang < liste.length - 1 ? liste[rang + 1] : null;
  const aller = useCallback((c: Resume | null) => { if (c) setOuverte(c.carteId); }, []);

  /* Les flèches du clavier parcourent la revue — le geste attendu quand on
     enchaîne 126 cartes. Ignorées si le focus est dans un champ : on ne veut
     pas changer de carte en corrigeant une valeur. */
  useEffect(() => {
    if (!ouverte) return;
    const surTouche = (e: KeyboardEvent) => {
      const cible = e.target as HTMLElement | null;
      if (cible && /^(INPUT|TEXTAREA|SELECT)$/.test(cible.tagName)) return;
      if (e.key === 'ArrowRight' && suivante) { e.preventDefault(); setOuverte(suivante.carteId); }
      if (e.key === 'ArrowLeft' && precedente) { e.preventDefault(); setOuverte(precedente.carteId); }
    };
    document.addEventListener('keydown', surTouche);
    return () => document.removeEventListener('keydown', surTouche);
  }, [ouverte, suivante, precedente]);

  /* Le filtre « Automate » porte le CODE d'inventaire ; les propositions se
     filtrent par identifiant de document. On traduit ici plutôt que d'ajouter
     un second filtre : deux sélecteurs pour la même intention divergeraient. */
  const filtreEquipId = useMemo(
    () => (filtreEquip ? resumes.find((r) => r.equipement === filtreEquip)?.equipementId || '' : ''),
    [filtreEquip, resumes],
  );

  const enRejet = recap?.rejet ?? 0;
  const aParametrer = recap?.sansCible ?? 0;

  const rafraichir = async () => {
    if (!detail) return;
    await chargerDetail(detail.carte.carteId, mode, bornes.depuis, bornes.jusqua, periodeDemandee);
    await chargerResumes();
  };

  const basculer = async (source: 'fournisseur' | 'interne') => {
    if (!detail) return;
    setMessage('');
    // R2 — la bascule rejuge la série : aperçu, puis motif si des verdicts changent.
    const r = await ecrireAvecApercu(`/api/ged/ged/cqi-cartes/${detail.carte.carteId}/params`, { sourceCible: source }, demanderTexte);
    if (!r) return;
    if (!r.ok) { setErreur((await r.json().catch(() => ({}))).error?.message || 'Bascule refusée.'); return; }
    setErreur('');
    const j = await r.json().catch(() => ({}));
    const dejaFigee = /cible figée .* reprise/.test(String((j.trace || []).join(' ')));
    setMessage(source === 'interne'
      ? (dejaFigee
        ? 'Cible interne reprise — celle approuvée sur ce lot, avec ses limites figées.'
        : 'Constitution de la cible interne ouverte : la carte est en BASELINE — aucune règle n’est évaluée et les repères sont provisoires, jusqu’à ce que la cible soit approuvée.')
      : 'Retour à la cible du certificat fournisseur.');
    await rafraichir();
  };

  /* R1 — APPROUVER, donc FIGER, la cible interne du lot ouvert. Le serveur
     refuse si la recevabilité échoue (motifs mot pour mot) : l'écran ne fait
     que les montrer avant le geste. */
  const [approbationOuverte, setApprobationOuverte] = useState(false);
  const approuverCible = async (commentaire: string) => {
    if (!detail) return false;
    setMessage('');
    const r = await fetchEcriture(`/api/ged/ged/cqi-cartes/${detail.carte.carteId}/periode/approuver-cible`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirme: true, commentaire: commentaire || undefined }),
    });
    if (!r.ok) { setErreur((await r.json().catch(() => ({}))).error?.message || 'Approbation refusée.'); return false; }
    setErreur('');
    setMessage('Cible interne approuvée et FIGÉE : saisir un passage ne déplace plus aucune limite. Une nouvelle cible se constituera sur un nouveau lot.');
    setApprobationOuverte(false);
    await rafraichir();
    return true;
  };

  /** N1-6 — les règles ne s'appliquent plus au clic sur la case : on prépare,
   *  puis on enregistre. Une case cochée n'est pas un enregistrement, et rien
   *  ne le disait ; on ne savait donc jamais si le changement avait été pris. */
  const enregistrerRegles = async () => {
    if (!detail || !brouillonRegles) return;
    setMessage('');
    // R2 — changer les règles rejuge toute la série : aperçu, motif si besoin.
    const r = await ecrireAvecApercu(`/api/ged/ged/cqi-cartes/${detail.carte.carteId}/params`, { regles: brouillonRegles }, demanderTexte);
    if (!r) return;
    if (!r.ok) { setErreur((await r.json().catch(() => ({}))).error?.message || 'Au moins une règle doit rester active.'); return; }
    const j = await r.json();
    setErreur('');
    setMessage(j.trace?.length ? `Règles enregistrées — ${j.trace.join(' · ')}` : 'Règles enregistrées.');
    setBrouillonRegles(null);
    await rafraichir();
  };

  /** Annulation motivée d'un passage ancien — RQ. Le point sort des cartes et
   *  des statistiques, la ligne reste en base, le journal porte le motif. */
  const annulerPassage = async (passageId: string) => {
    setMessage('');
    const r = await fetchEcriture(`/api/ged/ged/cqi-passages/${passageId}/annuler`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      // `responsable` : un RQ déjà habilité agit sans ce champ (revérifié sur
      // son propre profil côté serveur) ; sinon c'est lui qui l'authentifie
      // depuis ce poste, sans y ouvrir de session.
      body: JSON.stringify({ motif: motifAnnulation.trim(), responsable: authAnnulation.payload }),
    });
    if (!r.ok) { setErreur((await r.json().catch(() => ({}))).error?.message || 'Annulation refusée.'); return; }
    setErreur('');
    setMessage('Passage annulé — il sort des statistiques, la trace et le motif restent au journal.');
    setSelection(null); setAnnulerOuvert(false); setMotifAnnulation(''); authAnnulation.reinitialiser();
    await rafraichir();
    await chargerResumes();
  };

  /** Suite donnée à un passage : commentaire, validation, dérogation (N1-4, N1-14). */
  const suivrePassage = async (passageId: string, corps: Record<string, unknown>): Promise<boolean> => {
    setMessage('');
    const r = await fetchEcriture(`/api/ged/ged/cqi-passages/${passageId}/suite`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(corps),
    });
    if (!r.ok) { setErreur((await r.json().catch(() => ({}))).error?.message || 'Enregistrement refusé.'); return false; }
    setErreur('');
    setMessage('Suite enregistrée et tracée au journal.');
    setSelection(null);
    await rafraichir();
    return true;
  };

  /** Rejet manuel d'UN niveau — pas tout le passage (`annulerPassageCqi` reste
   *  le bon geste pour ça, réservé au RQ). N'importe pas le statut/les règles
   *  déjà rendus : voir `cqi-points.ts`, ils ne sont recalculés qu'à la
   *  prochaine écriture ou lecture. */
  const rejeterValeur = async (passageId: string, niveau: number, rejete: boolean, motif: string) => {
    setMessage('');
    const r = await fetchEcriture(`/api/ged/ged/cqi-passages/${passageId}/valeur/rejeter`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ niveau, rejete, motif }),
    });
    if (!r.ok) { setErreur((await r.json().catch(() => ({}))).error?.message || 'Enregistrement refusé.'); return; }
    setErreur('');
    setMessage(rejete ? 'Point rejeté et tracé au journal.' : 'Rejet levé et tracé au journal.');
    setSelection(null);
    await rafraichir();
  };

  const blocCourant = detail?.blocs?.[detail.blocs.length - 1] || null;
  const reglesAffichees = brouillonRegles ?? detail?.carte.reglesActives ?? [];
  const reglesModifiees = brouillonRegles !== null
    && JSON.stringify([...brouillonRegles].sort()) !== JSON.stringify([...(detail?.carte.reglesActives || [])].sort());

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
        {/* §7.3.5 « Contrôle qualité interne » — la clause de CET écran. La
            pastille annonçait 7.3.7, qui est celle des COMPARAISONS
            INTERLABORATOIRES (l'EEQ, écran voisin) : deux dispositifs distincts
            et non substituables, le CQI surveillant la stabilité au jour le
            jour, l'EEQ la justesse face aux pairs. Corrigé le 14/08/2026. */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <H1 eyebrow={<>Performance analytique · <RefISO code="7.3.7.2" prefixe="" /><Norme art="7.3.7.2" /></>} title="CQI — Cartes de contrôle" />
          {/* Ce qui est tracé se VOIT (24/08/2026) : la reprise des lots
              s'écrivait sous « CQI-LOTS » sans accès à l'écran. */}
          <BoutonJournal reference="CQI-LOTS" quoi="des reprises de lots" />
        </div>
        {/* Lot 3 (11/08/2026) — les 126 cartes venaient toutes de la reprise :
            un analyte mis en service après coup restait sans carte. */}
        {peutParametrer && <NouvelleCarte onCreee={() => void chargerResumes()} />}
      </div>

      {recap && (enRejet > 0 || aParametrer > 0 || recap.enAttente > 0 || (recap.baseline ?? 0) > 0 || (recap.lotsInutilisables ?? 0) > 0) && (
        <div style={{ display: 'grid', gap: 4, padding: '10px 14px', borderRadius: 'var(--radius-md)', background: enRejet ? 'var(--red-50)' : 'var(--amber-50)', border: `1px solid ${enRejet ? 'var(--red-200)' : 'var(--amber-300)'}` }}>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            <I n="alert" s={16} />
            <strong style={{ fontSize: 13, color: 'var(--text-strong)' }}>
              {enRejet} carte(s) en rejet · {recap.alerte} en avertissement · {aParametrer} sans cible
            </strong>
            {recap.enAttente > 0 && (
              <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: 'var(--red-50)', color: 'var(--red-600)', border: '1px solid var(--red-200)' }}>
                {recap.enAttente} passage(s) en attente d’une suite
              </span>
            )}
            {(recap.baseline ?? 0) > 0 && (
              <span title="Cible interne demandée, pas encore approuvée : aucune règle n’est évaluée sur ces cartes"
                style={{ fontSize: 11, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: 'var(--cyan-50)', color: 'var(--cyan-700)', border: '1px solid var(--cyan-200)' }}>
                {recap.baseline} carte(s) en baseline
              </span>
            )}
            {(recap.lotsInutilisables ?? 0) > 0 && (
              <span title="Lot de contrôle périmé ou hors période : la saisie y est refusée sans dérogation motivée"
                style={{ fontSize: 11, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: 'var(--red-50)', color: 'var(--red-600)', border: '1px solid var(--red-200)' }}>
                {recap.lotsInutilisables} lot(s) de contrôle inutilisable(s)
              </span>
            )}
          </div>
          <span style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
            Une carte sans cible ni écart-type ne contrôle rien : aucune règle ne peut s’y appliquer.
            {' '}Un passage non conforme reste <em>en attente</em> tant qu’il n’a pas reçu de suite —
            dérogation motivée, repasse, ou calibration.
          </span>
          {/* Ce décompte se REFAIT à chaque lecture depuis les passages : il suit
              une correction de saisie ou un changement de règles sans qu'on ait
              rien à relancer (N1-2). */}
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            Recalculé à chaque ouverture de l’écran sur les {recap.total} cartes — ce n’est pas un constat figé.
          </span>
        </div>
      )}

      <div style={{ ...card, padding: 12, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 220px', maxWidth: 340 }}>
          {/* La recherche portait déjà sur le code ET le nom de l'automate ;
              l'intitulé ne le disait pas, donc personne n'y tapait « EQ03 ». */}
          <label style={{ display: 'block', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 4 }} htmlFor="f-q">Rechercher un paramètre ou un automate</label>
          <input id="f-q" type="search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Glycémie, TSH, EQ03, Erba Lyte Pro, Roche…" style={inputStyle} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 4 }} htmlFor="f-eq">Automate</label>
          <Selecteur id="f-eq" value={filtreEquip} onChange={setFiltreEquip} style={{ ...inputStyle, minWidth: 230 }}>
            <option value="">Tous</option>
            {equipements.map((e) => { const [c, n] = e.split('|'); return <option key={c} value={c}>{c} · {n}</option>; })}
          </Selecteur>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 4 }} htmlFor="f-fo">Fournisseur du réactif</label>
          <Selecteur id="f-fo" value={filtreFourn} onChange={setFiltreFourn} style={{ ...inputStyle, minWidth: 200 }}>
            <option value="">Tous</option>
            {fournisseurs.map((f) => <option key={f} value={f}>{f}</option>)}
          </Selecteur>
          {/* Même précaution : pendant le chargement, « aucune carte n'est
              rattachée à un fournisseur » est une affirmation sur le
              paramétrage, pas sur l'attente. Elle a d'ailleurs fait soupçonner
              une jointure fautive alors que la liste était simplement vide
              faute de cartes (relevé BLOQ-01). */}
          {!attend && fournisseurs.length === 0 && (
            <span style={{ display: 'block', marginTop: 3, fontSize: 12.5, color: 'var(--text-muted)' }}>
              Aucune carte n’est encore rattachée à un fournisseur.
            </span>
          )}
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 4 }} htmlFor="f-et">État</label>
          <select id="f-et" value={filtreEtat} onChange={(e) => setFiltreEtat(e.target.value)} style={{ ...inputStyle, minWidth: 170 }}>
            <option value="">Tous</option>
            <option value="rejet">En rejet</option>
            <option value="alerte">En avertissement</option>
            <option value="conforme">Conformes</option>
            <option value="aparametrer">Sans cible</option>
          </select>
        </div>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)', paddingBottom: 8 }}>
          {attend ? '…' : `${liste.length} carte(s) · ${liste.reduce((a, b) => a + b.points, 0)} point(s)`}
        </span>
      </div>

      {attend && <div style={card}><Skeleton rows={4} height={46} /></div>}
      {!attend && resumes.length === 0 && (
        <div style={{ ...card, padding: 26, textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>
          {/* ⚠️ « Aucune carte » et « je n'ai pas pu lire » ne se disent pas
              pareil. C'est toute l'histoire de BLOQ-01 : un écran affirmait le
              premier alors que le second était vrai, et il n'y avait aucun
              moyen de s'en apercevoir depuis l'écran. */}
          {backendError
            ? 'Les cartes de contrôle n’ont pas pu être lues — le moteur ne répond pas. Ce n’est pas un registre vide.'
            : 'Aucune carte de contrôle.'}
        </div>
      )}

      <div style={{ display: 'grid', gap: 8 }}>
        {liste.map((r) => {
          const ton = TONS[r.statut] || TONS.conforme;
          const active = ouverte === r.carteId;
          return (
            <div key={r.carteId} style={{ ...card, padding: 0, overflow: 'hidden' }}>
              <button
                onClick={() => {
                  // Le détail est vidé ICI, au geste de fermeture — plus dans un
                  // effet. Sinon le détail de la carte précédente resterait
                  // affiché sous l'en-tête de la suivante le temps du chargement.
                  if (active) setDetail(null);
                  setOuverte(active ? null : r.carteId);
                }}
                aria-expanded={active}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '11px 14px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-strong)', minWidth: 200 }}>{r.analyteNom}</span>
                {/* Le code d'inventaire ne se reconnaît pas seul : le nom de
                    l'automate est donné à côté, dans son propre champ — on
                    appelle l'appareil par l'un ou par l'autre. */}
                <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{r.equipement}</span>
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)', minWidth: 120 }}>{r.equipementNom}</span>
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{r.nbNiveaux} niveaux · {r.points} points</span>
                {(r.fournisseur || r.reactifDesignation) && (
                  <span title={`Réactif : ${r.reactifDesignation || 'non renseigné'}${r.reactifRef ? ` (réf. ${r.reactifRef})` : ''}`}
                    style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                    {r.reactifDesignation || r.fournisseur}
                  </span>
                )}
                {/* Lot de RÉACTIF (coffret) du dernier passage saisi — distinct du
                    lot de contrôle (BD1/BD2) affiché plus loin. Visible ici même,
                    plus seulement dans une infobulle : c'est ce qu'on cherche en
                    premier lors d'un rappel de lot fournisseur. */}
                {r.lotReactif && (
                  <span title="Lot de réactif du dernier passage saisi"
                    style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-pill)', padding: '1px 8px' }}>
                    lot réactif {r.lotReactif}
                  </span>
                )}
                {/* Lot du MATÉRIAU DE CONTRÔLE (sérum BD1/BD2) de la période
                    OUVERTE — jamais la même valeur que le lot de réactif
                    ci-dessus (`ged-cqi-periode.lotControle` vs
                    `ged-cqi-passage.lotReactif`, deux colonnes distinctes). */}
                {r.lotControle && (
                  <span title="Lot du matériau de contrôle (sérum) de la période en cours"
                    style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--cyan-700)', border: '1px solid var(--cyan-200)', borderRadius: 'var(--radius-pill)', padding: '1px 8px' }}>
                    lot contrôle {r.lotControle}
                  </span>
                )}
                {r.aParametrer ? (
                  <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: 'var(--amber-50)', color: 'var(--amber-600)' }}>Sans cible</span>
                ) : (
                  <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: ton.bg, color: ton.fg }}>{ton.label}</span>
                )}
                {r.sourceCible === 'interne' && (
                  <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: 'var(--cyan-50)', color: 'var(--cyan-700)' }}>Cible interne</span>
                )}
                {r.baseline && (
                  <span title="Cible interne demandée, pas encore approuvée : aucune règle n’est évaluée"
                    style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: 'var(--cyan-50)', color: 'var(--cyan-700)', border: '1px dashed var(--cyan-400)' }}>
                    Baseline {r.pointsBaseline ?? 0}/{r.minPointsInterne}
                  </span>
                )}
                {r.lotUtilisable && !r.lotUtilisable.utilisable && (
                  <span title={r.lotUtilisable.motif}
                    style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: 'var(--red-50)', color: 'var(--red-600)' }}>
                    {r.lotUtilisable.nature === 'perime' ? 'Lot périmé' : 'Lot inutilisable'}
                  </span>
                )}
                {r.enAttente > 0 && (
                  <span title="Passages non conformes sans dérogation ni repasse"
                    style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: 'var(--red-50)', color: 'var(--red-600)' }}>
                    {r.enAttente} en attente
                  </span>
                )}
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{r.dernierPassage ? fmtDate(r.dernierPassage) : '—'}</span>
                <I n={active ? 'check' : 'diff'} s={13} />
              </button>

            </div>
          );
        })}
      </div>

      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, maxWidth: '82ch' }}>
        Axe vertical en σ. Les règles sont évaluées automatiquement et ne bloquent rien : BIO24
        signale et conserve la trace du traitement, la validation des résultats patients reste
        du ressort du SIL.
      </p>

      {/*
        CQI-1 — les non-conformités PROPOSÉES par le contrôle interne. Sa place
        est ici, sous les cartes : une proposition ne se juge pas dans le
        registre des NC (où elle n'existe pas encore) mais devant la carte qui
        l'a produite. Rien n'y est créé automatiquement — voir l'en-tête du
        composant.
      */}
      <section style={{ display: 'grid', gap: 10 }}>
        <h2 style={{ margin: 0, fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-strong)' }}>
          Signaux qualité — non-conformités proposées
        </h2>
        <PropositionsNc equipementId={filtreEquipId || undefined} />
      </section>

      {/*
        LES DEUX RÉGLAGES SONT PARTIS AU PARAMÉTRAGE (RQ, 24/08/2026) : « les
        règles d'acceptation des CQI et la liaison au référentiel doivent être
        au niveau du paramétrage, et non à la suite de toute la liste de CQI ».

        Ils étaient empilés ICI, sous les 126 cartes : changer une règle de
        Westgard demandait de traverser la liste entière. Et ni l'un ni l'autre
        ne parle d'une carte en particulier — le catalogue décrit ce que le
        moteur sait évaluer, la réconciliation ce que le manuel peut rattacher.

        ⚠️ On laisse un RENVOI plutôt que rien. Un bloc qui disparaît sans un mot
        se lit comme une régression : on le cherche là où il était, on ne le
        trouve pas, et on conclut qu'il a été supprimé. La phrase dit où il est
        allé, et le bouton y conduit.
      */}
      {peutReglerCqi && (
        <section style={{
          ...card, padding: '11px 14px', display: 'flex', gap: 12,
          alignItems: 'center', flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)', flex: 1, minWidth: 260 }}>
            Les <strong>règles d’acceptation</strong> et la <strong>liaison au référentiel</strong>
            {' '}se règlent au paramétrage, rubrique « Contrôle interne » — ce sont des réglages du
            module, pas des lignes de ce registre.
          </span>
          <Bouton taille="sm" onClick={() => openSettings('cqiregles')}>Ouvrir le paramétrage</Bouton>
        </section>
      )}

      {/* ── Calibration ouverte DEPUIS un point déviant ─────────────────────
          Elle porte la SÉRIE et la RÈGLE enfreinte : c'est ce qui la distingue
          d'une calibration de routine, et ce qui permet de répondre à la seule
          question que pose un évaluateur devant un rejet — « qu'avez-vous
          fait ? ». Les deux champs sont affichés en lecture seule : ils
          viennent du point, on ne les invente pas ici. */}
      {calibrerDepuis && (
        <CalibrationDepuisCqi
          contexte={calibrerDepuis}
          onFerme={() => setCalibrerDepuis(null)}
          onFaite={() => { setCalibrerDepuis(null); setMessage('Calibration enregistrée et rattachée au point déviant.'); }}
        />
      )}

      {/* La carte en FENÊTRE, avec sa navigation. `Modale` porte déjà Échap,
          le voile et le retour du focus — on n'en refait pas un second. */}
      {ouverte && detail && (
        <Modale
          titre={`${detail.carte.analyte} — ${detail.carte.equipement}`}
          sousTitre={(
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              {`${detail.carte.analyteNom || ''}${detail.carte.equipementNom ? ` · ${detail.carte.equipementNom}` : ''}${rang >= 0 ? ` — carte ${rang + 1} sur ${liste.length}` : ''}`}
              {/* Le journal de la carte : `CQI-<analyte>-<équipement>` est la clé
                  sous laquelle les actes de la carte sont écrits. */}
              <BoutonJournal reference={`CQI-${detail.carte.analyte}-${detail.carte.equipement}`} quoi="de cette carte" />
            </span>
          )}
          largeur={1100}
          onClose={() => setOuverte(null)}
          pied={(
            <div style={{ display: 'flex', gap: 10, width: '100%', alignItems: 'center' }}>
              <Bouton taille="sm" disabled={!precedente} onClick={() => aller(precedente)} title={precedente ? `${precedente.analyte} — ${precedente.equipement} (←)` : 'Première carte de la liste'}>
                ← Précédente
              </Bouton>
              <Bouton taille="sm" disabled={!suivante} onClick={() => aller(suivante)} title={suivante ? `${suivante.analyte} — ${suivante.equipement} (→)` : 'Dernière carte de la liste'}>
                Suivante →
              </Bouton>
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                Les flèches du clavier parcourent la revue.
              </span>
              <span style={{ flex: 1 }} />
              <Bouton taille="sm" onClick={() => setOuverte(null)}>Fermer</Bouton>
            </div>
          )}>
              <div style={{ padding: 0, display: 'grid', gap: 12 }}>
          {/* CE À QUOI LA CARTE SE RATTACHE (retour Ines du 17/08).
              Une carte ne connaît que son paramètre, son automate et son
              fournisseur — ni la technique, ni l'examen. Les deux se
              rejoignent PAR LE PARAMÈTRE, et ce sont exactement les questions
              qu'on se pose en lisant un rejet : sur quelle technique, et quel
              examen en dépend. */}
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 12.5, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: '9px 12px' }}>
            <span>
              <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginInlineEnd: 6 }}>Paramètre</span>
              <strong>{detail.carte.analyte}</strong>{detail.carte.analyteNom ? ` — ${detail.carte.analyteNom}` : ''}
            </span>
            <span>
              <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginInlineEnd: 6 }}>Automate</span>
              <strong>{detail.carte.equipement}</strong>{detail.carte.equipementNom ? ` — ${detail.carte.equipementNom}` : ''}
            </span>
            {/* La technique CONTRÔLÉE par la carte, et non la liste de celles
                du paramètre : c'est elle qui porte le principe et la
                performance attendue — un σ n'a de sens que rapporté à une
                méthode. Tant qu'elle n'est pas déclarée, on montre les
                candidates plutôt que rien, mais on le DIT. */}
            <span title={detail.carte.technique?.principe || ''}>
              <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginInlineEnd: 6 }}>Technique</span>
              {detail.carte.technique
                ? <><strong>{detail.carte.technique.code}</strong>{` — ${detail.carte.technique.nom}`}</>
                : detail.carte.techniques?.length
                  ? (
                    <span style={{ color: 'var(--amber-700)' }}>
                      à préciser — {detail.carte.techniques.map((t) => t.code).join(' ou ')}
                    </span>
                  )
                  : <span style={{ color: 'var(--amber-700)' }}>non rattachée</span>}
            </span>
            <span>
              <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginInlineEnd: 6 }}>Examens</span>
              {detail.carte.examens?.length
                ? detail.carte.examens.map((e) => e.code).join(' · ')
                : <span style={{ color: 'var(--amber-700)' }}>aucun au catalogue</span>}
            </span>
            {detail.carte.fournisseur && (
              <span>
                <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginInlineEnd: 6 }}>Réactif</span>
                {detail.carte.fournisseur}{detail.carte.reactifDesignation ? ` · ${detail.carte.reactifDesignation}` : ''}
              </span>
            )}
          </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Période</span>
                    <select value={fenetre} onChange={(ev) => setFenetre(ev.target.value as typeof fenetre)} style={{ ...inputStyle, fontSize: 12.5, padding: '4px 8px', width: 'auto' }}>
                      <option value="lot">Le lot en cours</option>
                      <option value="7j">7 jours</option>
                      <option value="30j">30 jours</option>
                      <option value="90j">90 jours</option>
                      <option value="12mois">12 mois</option>
                      <option value="tout">Tout l’historique</option>
                      <option value="perso">Personnalisé…</option>
                      {/* ⚠️ « CARTES ARCHIVÉES » (12/09/2026) — l'entrée qui
                          manquait. Les six autres choix disent COMBIEN DE TEMPS
                          on regarde ; celui-ci dit QUEL LOT. Ce n'est pas la
                          même question, et c'est pourquoi « Tout l'historique »
                          n'y répondait pas : il fond tous les lots dans une
                          seule courbe au lieu de rendre la carte d'un lot. */}
                      {archivees.length > 0 && (
                        <option value="archive">Cartes archivées ({archivees.length})</option>
                      )}
                    </select>
                  </label>
                  {/* Le choix du lot archivé — daté, parce qu'on cherche une
                      période et non un identifiant de lot. */}
                  {fenetre === 'archive' && (
                    <select value={periodeOuverte} onChange={(ev) => setPeriodeOuverte(ev.target.value)}
                      aria-label="Lot archivé à ouvrir"
                      style={{ ...inputStyle, fontSize: 12.5, padding: '4px 8px', width: 'auto', maxWidth: 340 }}>
                      <option value="">— choisir un lot —</option>
                      {archivees.map((p) => (
                        <option key={p.periodeId} value={p.periodeId}>
                          {`lot ${p.lotControle || '?'}`}
                          {p.dateDebut || p.dateFin ? ` · ${p.dateDebut ? fmtDate(p.dateDebut) : '?'} → ${p.dateFin ? fmtDate(p.dateFin) : '?'}` : ''}
                          {typeof p.points === 'number' ? ` · ${p.points} point(s)` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                  {fenetre === 'perso' && (
                    <>
                      <input type="date" value={persoDepuis} onChange={(ev) => setPersoDepuis(ev.target.value)} style={{ ...inputStyle, fontSize: 12.5, padding: '4px 8px', width: 'auto' }} aria-label="Depuis" />
                      <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>au</span>
                      <input type="date" value={persoJusqua} onChange={(ev) => setPersoJusqua(ev.target.value)} style={{ ...inputStyle, fontSize: 12.5, padding: '4px 8px', width: 'auto' }} aria-label="Jusqu’au" />
                    </>
                  )}
                  <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                    {fenetre === 'archive'
                      /* ⚠️ ON DIT QUE C'EST UNE ARCHIVE. Une carte clôturée
                         ressemble trait pour trait à une carte en cours : sans
                         cette phrase, on lirait un rejet vieux de six mois comme
                         un rejet du jour. */
                      ? (periodeOuverte
                        /* ⚠️ LA PHRASE DIT « LECTURE SEULE » DEPUIS LE
                           13/09/2026, et c'est maintenant VRAI. Le 12/09 elle
                           ne le disait pas, faute de garde : aucune route ne
                           refusait alors d'écrire sur un lot clôturé, et
                           annoncer une protection qui n'existe pas est
                           exactement l'affirmation fausse que ce chantier
                           corrige. L'arbitrage de la RQ a tranché, les quatre
                           chemins d'écriture sont fermés (`refusLotCloture`),
                           et la phrase a suivi — dans cet ordre. */
                        ? 'Lot CLÔTURÉ, en LECTURE SEULE — la carte telle qu’elle était, avec la cible et les limites de ce lot-là. Rien ne s’y saisit, ne s’y déroge ni ne s’y rejette : le moteur le refuse.'
                        : 'Choisissez le lot à rouvrir. Les points, les règles et la cible seront ceux de ce lot.')
                      : mode === 'lot'
                        ? 'Lot en cours seulement — les limites correspondent à sa cible.'
                        : `${detail.periodes.length} lot(s) sur la fenêtre — chaque point normalisé sur la cible du sien.`}
                  </span>
                  <span style={{ flex: 1 }} />
                  {/* R1 — DEMANDER la cible interne ouvre sa CONSTITUTION (baseline) ;
                      l'APPROUVER la fige. Deux gestes, deux droits. Un bouton
                      indisponible dit pourquoi (R10). */}
                  {blocCourant && !blocCourant.cloturee && (
                    <Bouton taille="sm" onClick={() => setApprobationOuverte(true)} disabled={estOccupe()}
                      title={blocCourant.baseline
                        ? 'Série, moyenne, σ et CV provisoires, constats de recevabilité, puis approbation si elle est recevable'
                        : blocCourant.sourceCible === 'interne'
                          ? `Cible interne approuvée le ${blocCourant.approuveeLe ? fmtDate(blocCourant.approuveeLe) : '?'} — consultation`
                          : 'Ce qu’une cible interne vaudrait aujourd’hui sur ce lot (consultation ; demander la cible interne pour la constituer)'}>
                      {blocCourant.baseline ? 'Consulter ou approuver la cible interne' : 'Consulter la cible interne'}
                    </Bouton>
                  )}
                  {peutParametrer && blocCourant && (
                    <Bouton taille="sm" onClick={() => void run('src', () => basculer(blocCourant.sourceCible === 'interne' ? 'fournisseur' : 'interne'))} disabled={estOccupe()}
                      title={blocCourant.sourceCible === 'interne'
                        ? 'Les limites redeviennent celles du certificat fournisseur ; la cible interne approuvée sur ce lot reste conservée'
                        : 'Ouvre la constitution d’une cible interne : la carte passe en baseline (aucune règle évaluée) jusqu’à l’approbation'}>
                      {blocCourant.sourceCible === 'interne' ? 'Revenir à la cible fournisseur' : 'Demander une cible interne'}
                    </Bouton>
                  )}
                </div>

                {/* R3 — un lot qui n'est plus valable AUJOURD'HUI : la saisie y
                    sera refusée sans dérogation motivée. Dit ici, avant la saisie. */}
                {blocCourant?.lotUtilisable && !blocCourant.lotUtilisable.utilisable && !blocCourant.cloturee && (
                  <div style={{ padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--red-50)', border: '1px solid var(--red-200)', display: 'grid', gap: 3 }}>
                    <strong style={{ fontSize: 12.5, color: 'var(--red-600)' }}>Lot de contrôle inutilisable — {blocCourant.lotUtilisable.motif}</strong>
                    <span style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
                      Aucun passage ne s’y enregistre sans une dérogation motivée (droit « cqiDeroger »). Ouvrez un nouveau lot de contrôle ci-dessous.
                    </span>
                  </div>
                )}

                {/* R1 — BASELINE EN COURS : ce qui manque, niveau par niveau. */}
                {blocCourant?.baseline && (
                  <div style={{ padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--cyan-50)', border: '1px dashed var(--cyan-400)', display: 'grid', gap: 4 }}>
                    <strong style={{ fontSize: 12.5, color: 'var(--cyan-700)' }}>
                      Baseline en cours — {Math.min(...blocCourant.statsInternes.filter((s) => !blocCourant.cibles.find((c) => c.qualitatif && c.niveau === s.niveau)).map((s) => s.n), blocCourant.points.length)}/{blocCourant.pointsRequis ?? detail.carte.minPointsInterne} points
                    </strong>
                    <span style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
                      La cible interne a été demandée mais n’est pas approuvée : <strong>aucune règle n’est évaluée</strong>, les points
                      constituent la cible et les repères du graphe sont provisoires (cible fournisseur en repli).
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      {blocCourant.cibles.filter((c) => !c.qualitatif).map((c) => {
                        const s = blocCourant.statsInternes.find((x) => x.niveau === c.niveau);
                        const requis = blocCourant.pointsRequis ?? detail.carte.minPointsInterne;
                        const manque = Math.max(0, requis - (s?.n ?? 0));
                        return `${(detail.carte.libellesNiveaux || [])[c.niveau - 1]?.trim() || `Niveau ${c.niveau}`} : ${s?.n ?? 0} point(s)${manque ? `, ${manque} manquant(s)` : ' — effectif atteint'}`;
                      }).join(' · ')}
                    </span>
                  </div>
                )}

                {/* ⚠️ Recentrer les limites sur une moyenne fausse rend la carte
                    verte sans rien résoudre : la bascule est REFUSÉE, et le
                    motif est écrit. Le serveur applique le même refus — l'écran
                    explique, il ne garde pas la porte. */}
                {blocCourant && blocCourant.baseline
                  && blocCourant.recevabiliteInterne && !blocCourant.recevabiliteInterne.recevable && (
                  <div style={{ padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--amber-50)', border: '1px solid var(--amber-300)', display: 'grid', gap: 4 }}>
                    <strong style={{ fontSize: 12.5, color: 'var(--text-strong)' }}>
                      Ce qui empêche encore d’approuver la cible interne
                    </strong>
                    {blocCourant.recevabiliteInterne.motifs.map((m, i) => (
                      <span key={i} style={{ fontSize: 12.5, color: 'var(--text-body)' }}>· {m}</span>
                    ))}
                  </div>
                )}

                {blocCourant?.sourceCible === 'interne' && !blocCourant.baseline && (
                  <div style={{ padding: '8px 12px', borderRadius: 'var(--radius-md)', background: 'var(--cyan-50)', border: '1px solid var(--cyan-200)', fontSize: 12.5, color: 'var(--text-body)' }}>
                    Cible interne <strong>figée</strong> — approuvée le {blocCourant.approuveeLe ? fmtDate(blocCourant.approuveeLe) : '?'}
                    {blocCourant.approuveeParNom ? ` par ${blocCourant.approuveeParNom}` : ''} sur{' '}
                    {(blocCourant.ciblesInternesFigees || []).map((f) => `${(detail.carte.libellesNiveaux || [])[f.niveau - 1]?.trim() || `N${f.niveau}`} : ${f.n} points`).join(', ') || '?'}.
                    Les limites ne bougent plus avec les nouveaux points ; une nouvelle cible se constitue sur un nouveau lot.
                    {blocCourant.ciblesEffectives?.some((e) => e.repli) && (
                      <strong style={{ display: 'block', marginTop: 3, color: 'var(--amber-600)' }}>
                        {blocCourant.ciblesEffectives.filter((e) => e.repli).map((e) => `${(detail.carte.libellesNiveaux || [])[e.niveau - 1]?.trim() || `Niveau ${e.niveau}`} : ${e.repli}`).join(' · ')}
                      </strong>
                    )}
                  </div>
                )}

                {approbationOuverte && blocCourant && (
                  <ApprobationCibleInterne
                    bloc={blocCourant} libelles={detail.carte.libellesNiveaux || []} unite={detail.carte.unite}
                    pointsRequis={blocCourant.pointsRequis ?? detail.carte.minPointsInterne}
                    peutApprouver={peutApprouverCible} occupe={estOccupe()}
                    onClose={() => setApprobationOuverte(false)}
                    onApprouver={(commentaire) => run('approuver', () => approuverCible(commentaire))}
                  />
                )}

                {peutParametrer && detail.carte.actif && (
                  <ArchiverCarteCqi
                    carteId={detail.carte.carteId} aUnLotOuvert={(detail.periodes || []).some((p) => !p.cloturee)}
                    onFait={() => { setOuverte(null); void chargerResumes(); setMessage('Carte archivée — elle a quitté les cartes en cours.'); }}
                  />
                )}

                {(() => {
                  /* Le lot EN COURS (refonte CQI L1) : ses informations, et le
                     geste pour les compléter sans ouvrir de nouvelle période. */
                  const enCours = (detail.blocs || []).find((b) => !b.cloturee);
                  return enCours ? (
                    <InfosLotCqi bloc={enCours} nbNiveaux={detail.carte.nbNiveaux} peutModifier={peutParametrer}
                      onFait={() => { setMessage('Informations du lot enregistrées.'); void rafraichir(); }} />
                  ) : null;
                })()}

                {peutParametrer && (
                  <NouveauLotCqi
                    carteId={detail.carte.carteId} nbNiveaux={detail.carte.nbNiveaux} unite={detail.carte.unite}
                    onCree={() => {
                      setFenetre('lot'); setPeriodeOuverte('');
                      void chargerResumes();
                      /* Un lot NEUF vient d'être ouvert : on revient au lot en
                         cours, donc sans période demandée — rester sur une
                         période archivée montrerait l'ancienne carte juste
                         après avoir créé la nouvelle. */
                      void chargerDetail(detail.carte.carteId, 'lot', '', '', '');
                    }}
                  />
                )}

                {/* Niveaux affichés et fenêtre de temps. Superposer trois
                    niveaux rend illisible la dérive d'un seul ; et sept mille
                    passages écrasent les derniers mois contre le bord droit. */}
                {detail.blocs.some((b) => b.points.length) && (
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5 }}>
                    <span style={{ color: 'var(--text-muted)' }}>Niveaux</span>
                    {Array.from({ length: detail.carte.nbNiveaux }, (_, k) => k + 1).map((n) => {
                      const vus = niveauxVus ?? Array.from({ length: detail.carte.nbNiveaux }, (_, k) => k + 1);
                      const on = vus.includes(n);
                      return (
                        <Bouton variante={on ? 'primaire' : 'contour'} taille="sm" key={n} onClick={() => {
                            const suiv = on ? vus.filter((x) => x !== n) : [...vus, n].sort();
                            // Tout décocher n'affiche rien : on retombe sur « tous ».
                            setNiveauxVus(suiv.length ? suiv : null);
                          }}>
                          {(detail.carte.libellesNiveaux || [])[n - 1] || `N${n}`}
                        </Bouton>
                      );
                    })}
                    {niveauxVus && (
                      <Bouton taille="sm" onClick={() => setNiveauxVus(null)}>Tous</Bouton>
                    )}
                    <span style={{ flex: 1 }} />
                    <span style={{ color: 'var(--text-muted)' }}>Fenêtre</span>
                    {([['Tout', 0], ['Dernière moitié', 0.5], ['Dernier quart', 0.75], ['Dernier dixième', 0.9]] as const).map(([l, d]) => (
                      <Bouton variante={zoom.debut === d && zoom.fin === 1 ? 'primaire' : 'contour'} taille="sm" key={l} onClick={() => setZoom({ debut: d, fin: 1 })}>{l}</Bouton>
                    ))}
                  </div>
                )}

                {/* Le graphe et le détail du point CÔTE À CÔTE (RQ, 18/08 :
                    « quand je clique sur un point je veux voir les détails à
                    droite du graphe »). Sous le graphe, il fallait quitter la
                    courbe des yeux pour lire ce qu'on venait d'y désigner.
                    ⚠️ 06/09/2026 (Inès) : `minmax(0, 1fr)` laissait le graphe
                    RÉTRÉCIR pour faire de la place au panneau — les bandes de
                    σ et les points se resserraient au clic, l'inverse de ce
                    qu'on veut au moment de LIRE un point. Le graphe garde
                    maintenant une largeur plancher ; c'est la ligne entière
                    qui déborde (scroll horizontal) plutôt que la courbe qui
                    se comprime. */}
                {/* Lot D (09/10/2026) — le panneau du point passe À DROITE quand la
                    place existe, et SOUS la courbe, pleine largeur, quand elle
                    manque (fenêtre de revue, écran étroit) : plus de panneau
                    coincé dans une colonne de 300 px sous le pli. */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ flex: '1 1 640px', minWidth: 0 }}>
                {detail.blocs.some((b) => b.points.length) ? (
                  <LeveyJennings blocs={detail.blocs} nbNiveaux={detail.carte.nbNiveaux}
                    onPoint={choisirPoint} selection={selection}
                    unite={detail.carte.unite} libelles={detail.carte.libellesNiveaux || []}
                    niveauxVisibles={niveauxVus ?? undefined} zoom={zoom}
                    rejetAffichage={detail.rejetAffichage} />
                ) : (
                  <div style={{ padding: 20, textAlign: 'center', fontSize: 12.5, color: 'var(--text-muted)' }}>
                    Aucun point sur cette période.
                  </div>
                )}
                </div>

                {selection && (
                  <div style={{ flex: '1 1 340px', maxWidth: '100%', padding: '14px 16px', background: 'var(--cyan-50)', border: '1px solid var(--cyan-200)', borderRadius: 'var(--radius-md)', display: 'grid', gap: 10, minWidth: 0 }}>
                    {/* En-tête séparé des valeurs (06/09/2026, Inès : « peu
                        lisible ») — date/série, valeurs et bouton de rejet
                        cohabitaient sur une seule ligne qui repliait sans
                        fin en colonne étroite. */}
                    <strong style={{ fontSize: 14, color: 'var(--cyan-700)', paddingBottom: 8, borderBottom: '1px solid var(--cyan-200)' }}>
                      {fmtDate(selection.date)}{selection.numeroSerie ? ` · série n°${selection.numeroSerie}` : ''}
                    </strong>
                    {/* Le matériau de ce point (03/10/2026) : quel aliquot, de quel flacon. */}
                    <AliquotsDuPoint carteId={detail.carte.carteId} passageId={selection.passageId} />
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {selection.valeurs.map((v) => (
                        <span key={v.niveau} style={{ fontSize: 13, fontFamily: 'var(--font-mono)', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px', background: 'var(--surface-card)', border: '1px solid var(--cyan-200)', borderRadius: 'var(--radius-sm)', textDecoration: v.rejete ? 'line-through' : 'none' }}>
                          N{v.niveau} <strong>{v.texte ?? v.valeur}</strong>
                          {v.qualitatif && v.conforme !== undefined && v.conforme !== null && (
                            <span style={{ color: v.conforme ? 'var(--green-600)' : 'var(--red-600)' }}>{v.conforme ? 'conforme' : 'hors critère'}</span>
                          )}
                          {typeof v.z === 'number' && <span style={{ color: Math.abs(v.z) >= 3 ? 'var(--red-600)' : Math.abs(v.z) >= 2 ? 'var(--amber-600)' : 'var(--green-600)' }}>{v.z >= 0 ? '+' : ''}{v.z.toFixed(2)}σ</span>}
                          <Bouton taille="sm"
                            onClick={async () => {
                              if (v.rejete) { void run(`rej-${v.niveau}`, () => rejeterValeur(selection.passageId, v.niveau, false, '')); return; }
                              const motif = await demanderTexte('Motif du rejet de ce point (5 caractères au minimum) :',
                                { titre: 'Rejeter ce point', confirmer: 'Rejeter', min: 5 });
                              if (motif) void run(`rej-${v.niveau}`, () => rejeterValeur(selection.passageId, v.niveau, true, motif));
                            }}
                            disabled={estOccupe()}
                            title={v.rejete ? (v.rejeteMotif ? `Rejeté — ${v.rejeteMotif}` : 'Rejeté') : 'Rejeter ce point (exclu des statistiques, journalisé)'}
                            >
                            {v.rejete ? 'Reprendre' : 'Rejeter'}
                          </Bouton>
                        </span>
                      ))}
                      {selection.importe && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>repris du tableur</span>}
                      {/* Le lot de réactif du point — c'est ici qu'on le lit
                          quand le trait ambre de la courbe a fait poser la
                          question « lequel, au fait ? ». Absent sur la plupart
                          des passages repris : on ne l'invente pas, on ne
                          l'affiche simplement pas. */}
                      {!!selection.lotReactif && (
                        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                          réactif <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-body)' }}>{selection.lotReactif}</strong>
                        </span>
                      )}
                    </div>
                    {selection.regles.length > 0 && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {selection.regles.map((g, i) => (
                          <span key={i} title={g.libelle}
                            style={{ fontSize: 10.5, fontWeight: 700, fontFamily: 'var(--font-mono)', borderRadius: 'var(--radius-pill)', padding: '2px 8px', background: g.gravite === 'rejet' ? 'var(--red-50)' : 'var(--amber-50)', color: g.gravite === 'rejet' ? 'var(--red-600)' : 'var(--amber-600)' }}>
                            {g.code}{g.niveau ? ` · N${g.niveau}` : ''}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* N1-14 — le point non conforme dit ce qu'il ATTEND, et de
                        qui. Un rouge sans suite n'est pas un dossier clos. */}
                    {/* R2 — VERDICT D'ORIGINE ET RÉÉVALUATIONS. Le verdict courant est
                        recalculé à chaque lecture ; ce bloc est sa contrepartie en
                        preuve : ce que le point valait quand il a été enregistré, et
                        chaque geste qui l'a changé depuis, avec son motif. */}
                    {(selection.statutOrigine || (selection.reevaluations && selection.reevaluations.length > 0)) && (
                      <div style={{ display: 'grid', gap: 4, padding: '8px 10px', borderRadius: 'var(--radius-md)', background: selection.verdictRevise ? 'var(--cyan-50)' : 'var(--surface-sunken)', border: `1px ${selection.verdictRevise ? 'dashed var(--cyan-400)' : 'solid var(--border-default)'}`, fontSize: 12 }}>
                        <strong style={{ color: selection.verdictRevise ? 'var(--cyan-700)' : 'var(--text-strong)' }}>
                          {selection.verdictRevise ? 'Verdict révisé' : 'Verdict d’origine conservé'}
                        </strong>
                        {selection.statutOrigine && (
                          <span style={{ color: 'var(--text-body)' }}>
                            À l’enregistrement{selection.verdictFigeLe ? ` (${fmtDate(selection.verdictFigeLe)})` : ''} : <strong>{TONS[selection.statutOrigine]?.label || selection.statutOrigine}</strong>
                            {selection.reglesOrigine?.length ? ` — ${selection.reglesOrigine.map((g) => g.code).join(', ')}` : ''}
                            {selection.cibleOrigine?.length ? ` · cible ${selection.cibleOrigine.map((c) => `N${c.niveau} ${c.cible} ± ${c.sd} (${c.source})`).join(', ')}` : ''}
                            {' '}· aujourd’hui : <strong>{TONS[selection.statut]?.label || selection.statut}</strong>
                          </span>
                        )}
                        {(selection.reevaluations || []).map((r, i) => (
                          <span key={i} style={{ color: 'var(--text-body)' }}>
                            {fmtDate(r.le)} — {r.parNom} · {r.changement} : {r.statutAvant} → {r.statutApres} — <em>{r.motif}</em>
                          </span>
                        ))}
                      </div>
                    )}
                    {/* R1 — un point de baseline n'a PAS de verdict : le dire, sinon
                        « aucune règle déclenchée » se lirait « conforme ». */}
                    {selection.statut === 'baseline' && (
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--cyan-700)' }}>
                        Point de baseline — aucune règle évaluée : il constitue la cible interne, qui n’est pas encore approuvée.
                      </div>
                    )}
                    {selection.exige && (
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: selection.couleur === 'rouge' ? 'var(--red-600)' : 'var(--amber-600)' }}>
                        {selection.enAttente ? '⏳ ' : ''}{selection.exige}
                      </div>
                    )}
                    {/* R6 — la conduite tenue : seulement sur un REJET (ou un point déjà instruit). */}
                    {(selection.statut === 'rejet' || selection.conduite?.cause) && !blocCourant?.cloturee && (
                      <ConduiteRejet point={selection} carteId={detail.carte.carteId}
                        peutSaisir={peut(settings?.perms, session.profile, 'cqiSaisir')}
                        peutValider={peut(settings?.perms, session.profile, 'cqiValider')}
                        peutDeroger={estBiologiste(session.profile, settings?.perms)}
                        personnes={(settings?.gedUsers || []).map((u) => ({ id: u.id, nom: u.nom }))}
                        onFait={async (m) => { setMessage(m); await rafraichir(); }}
                        onNouveauPassage={() => naviguerVers('cqisaisie')} />
                    )}
                    {selection.suite && (
                      <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                        Suite enregistrée : {selection.suite === 'derogation' ? 'dérogation' : selection.suite === 'repasse' ? 'repassé le même jour' : 'validé'}
                        {selection.motifDerogation ? ` — ${selection.motifDerogation}` : ''}
                      </div>
                    )}

                    {/* ── CALIBRER POUR TENTER DE RÉSOUDRE (14/08/2026) ────
                        Demandé par la RQ : depuis un point déviant, ouvrir
                        une calibration « en plus des calibrations à fréquence
                        recommandée ».

                        Ce n'est pas un raccourci de saisie, c'est une PREUVE :
                        devant un rejet de Westgard, l'évaluateur demande
                        « qu'avez-vous fait ? ». Une calibration créée depuis
                        ce point porte sa série et la règle enfreinte ; créée
                        depuis l'écran des calibrations, elle se serait
                        confondue avec la routine et n'aurait rien démontré.

                        Offert sur les points qui DÉVIENT seulement : proposer
                        une calibration sur un point conforme inviterait à
                        recalibrer sans raison — et une calibration de plus
                        n'est jamais neutre, elle engage les résultats. */}
                    {selection.regles.length > 0 && (
                      <Bouton taille="sm" onClick={() => setCalibrerDepuis({
                          equipementCode: detail?.carte.equipement || '',
                          analyteLibelle: detail?.carte.analyteNom || detail?.carte.analyte || '',
                          analyteCode: detail?.carte.analyte || '',
                          serie: `${selection.date}${selection.numeroSerie ? ` — série n°${selection.numeroSerie}` : ''}`,
                          regle: selection.regles.map((g) => g.code).join(', '),
                        })} title="Ouvrir une calibration rattachée à CE point : elle portera la série et la règle enfreinte." style={{ justifySelf: 'start' }}>
                        <I n="scale2" s={13} />Calibrer pour tenter de résoudre
                      </Bouton>
                    )}

                    {/* N1-4 / N1-11 — le commentaire se modifie, et la
                        modification part au journal chaîné avec l'ancienne
                        valeur. On corrige, on ne réécrit pas l'histoire. */}
                    <div style={{ display: 'grid', gap: 6, marginTop: 4 }}>
                      <label style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)' }} htmlFor="cqi-comm">
                        Commentaire
                      </label>
                      <textarea id="cqi-comm" value={commentaire} rows={2}
                        onChange={(e) => setCommentaire(e.target.value)}
                        placeholder={selection.couleur === 'rouge' ? 'Obligatoire sur un rejet : ce qui a été constaté, et ce qui a été fait.' : 'Facultatif'}
                        style={{ ...inputStyle, fontSize: 12.5, resize: 'vertical' }} />
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Bouton taille="sm" onClick={() => void run('cm', () => suivrePassage(selection.passageId, { commentaire }))} disabled={estOccupe() || commentaire === (selection.commentaire || '')}>
                          {estOccupe('cm') ? 'Enregistrement…' : 'Enregistrer le commentaire'}
                        </Bouton>
                        {selection.couleur === 'jaune' && selection.enAttente && (
                          <Bouton variante="primaire" taille="sm" onClick={() => void run('vl', () => suivrePassage(selection.passageId, { commentaire, validation: 'valide' }))} disabled={estOccupe()}>
                            <I n="check" s={13} />Valider cet avertissement
                          </Bouton>
                        )}
                      </div>
                      {selection.couleur === 'rouge' && selection.validation !== 'derogation' && (
                        <div style={{ display: 'grid', gap: 6, padding: '8px 10px', borderRadius: 'var(--radius-md)', background: 'var(--red-50)', border: '1px solid var(--red-200)' }}>
                          <label style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--red-600)' }} htmlFor="cqi-derog">
                            Dérogation — motif obligatoire
                          </label>
                          <input id="cqi-derog" value={motifDerogation} onChange={(e) => setMotifDerogation(e.target.value)}
                            placeholder="Pourquoi ce rejet est-il accepté malgré tout ?" style={{ ...inputStyle, fontSize: 12.5 }} />
                          {/* Non dérogeable sauf pour qui porte déjà le droit — ou un
                              responsable qui s'authentifie ici même (06/09/2026). */}
                          {!peutDeroger && <ChampsResponsable {...authDerogation} libelle="cette dérogation" />}
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Bouton taille="sm" onClick={() => void run('dg', () => suivrePassage(selection.passageId, {
                                commentaire, validation: 'derogation', motifDerogation, responsable: authDerogation.payload,
                              }).then((ok) => { if (ok) authDerogation.reinitialiser(); }))} disabled={estOccupe() || motifDerogation.trim().length < 10 || (!peutDeroger && !authDerogation.pret)}>
                              Déroger sous ce motif
                            </Bouton>
                            <span style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
                              Autrement : repasser le contrôle après recalibration — la repasse solde le
                              rejet d’elle-même, sans effacer sa trace.
                            </span>
                          </div>
                        </div>
                      )}
                      {/* Reliquat soldé le 12/08/2026 — un point aberrant découvert
                          après coup (mauvais flacon, mauvaise carte) s'annule avec
                          motif : il sort des statistiques, la ligne reste en base.
                          Un passage cité par une NC est refusé par le moteur. */}
                      {/* Réservée au RQ — ou à un responsable qui s'authentifie ici
                          même pour ce seul geste (06/09/2026). Visible de tous
                          désormais : c'est le mot de passe du responsable qui
                          protège le geste, plus l'écran lui-même. */}
                      {!annulerOuvert && (
                        <Bouton taille="sm" onClick={() => setAnnulerOuvert(true)} style={{ justifySelf: 'start' }}>
                          Annuler ce passage…
                        </Bouton>
                      )}
                      {annulerOuvert && (
                        <div style={{ display: 'grid', gap: 6, padding: '8px 10px', borderRadius: 'var(--radius-md)', background: 'var(--red-50)', border: '1px solid var(--red-200)' }}>
                          <label style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--red-600)' }} htmlFor="cqi-annul">
                            Annuler ce passage — motif obligatoire
                          </label>
                          <input id="cqi-annul" value={motifAnnulation} onChange={(e) => setMotifAnnulation(e.target.value)}
                            placeholder="Mauvais flacon, saisie sur la mauvaise carte…" style={{ ...inputStyle, fontSize: 12.5 }} />
                          {!estRq && <ChampsResponsable {...authAnnulation} libelle="cette annulation" />}
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Bouton taille="sm" onClick={() => { setAnnulerOuvert(false); setMotifAnnulation(''); authAnnulation.reinitialiser(); }}>Annuler</Bouton>
                            <Bouton variante="danger" taille="sm" onClick={() => void run('an', () => annulerPassage(selection.passageId))} disabled={estOccupe() || motifAnnulation.trim().length < 5 || (!estRq && !authAnnulation.pret)}>
                              {estOccupe('an') ? 'Annulation…' : 'Annuler ce passage'}
                            </Bouton>
                            <span style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
                              Le point sort des cartes et des statistiques ; la ligne et le motif restent au journal.
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                </div>

                {blocCourant && <Performance bloc={blocCourant} unite={detail.carte.unite} libelles={detail.carte.libellesNiveaux || []}
                  carteId={peutParametrer ? detail.carte.carteId : undefined}
                  onCritereModifie={peutParametrer ? (m) => { setMessage(m); void chargerResumes(); void chargerDetail(detail.carte.carteId, 'lot', '', '', ''); } : undefined} />}

                {peutParametrer && (
                  <div>
                    <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 5 }}>
                      Règles appliquées à cette carte
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {detail.catalogue.map((rg) => {
                        const on = reglesAffichees.includes(rg.code);
                        return (
                          <label key={rg.code} title={rg.description}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontFamily: 'var(--font-mono)', fontWeight: 700, background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-pill)', padding: '4px 9px', cursor: 'help' }}>
                            <input type="checkbox" checked={on}
                              onChange={() => setBrouillonRegles(on
                                ? reglesAffichees.filter((c) => c !== rg.code)
                                : [...reglesAffichees, rg.code])}
                              style={{ accentColor: 'var(--color-primary)' }} />
                            {rg.code}
                            <span style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, color: rg.gravite === 'rejet' ? 'var(--red-600)' : 'var(--amber-600)' }}>
                              {rg.gravite === 'rejet' ? 'rejet' : 'alerte'}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                    {/* N1-6 — cocher n'enregistre pas. Tant que ce bandeau est
                        là, le changement n'est PAS pris en compte, et il le dit. */}
                    {reglesModifiees ? (
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 8, padding: '8px 10px', borderRadius: 'var(--radius-md)', background: 'var(--amber-50)', border: '1px solid var(--amber-300)' }}>
                        <strong style={{ fontSize: 12.5, color: 'var(--text-strong)' }}>Modification non enregistrée</strong>
                        <Bouton taille="sm" onClick={() => setBrouillonRegles(null)}>
                          Annuler
                        </Bouton>
                        <Bouton variante="primaire" taille="sm" onClick={() => void run('rg', enregistrerRegles)} disabled={estOccupe()}>
                          <I n="check" s={13} />{estOccupe('rg') ? 'Enregistrement…' : 'Enregistrer les règles'}
                        </Bouton>
                        <span style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
                          Les points seront réévalués sur tout le lot, et le changement inscrit au journal.
                        </span>
                      </div>
                    ) : (
                      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 6 }}>
                        {detail.carte.reglesActives.length} règle(s) active(s) — enregistrées.
                      </div>
                    )}
                  </div>
                )}

                {/* N1-3 — les champs propres de la carte, dont l'automate. */}
                {peutParametrer && (
                  editionCarte ? (
                    <EditionCarte detail={detail}
                      onFini={(msg) => { setEditionCarte(false); if (msg) setMessage(msg); void rafraichir(); }}
                      onErreur={setErreur}
                      onAnnuler={() => setEditionCarte(false)} />
                  ) : (
                    <Bouton taille="sm" onClick={() => { setEditionCarte(true); setErreur(''); }} style={{ justifySelf: 'start' }}>
                      <I n="gear" s={13} />Modifier la carte (automate, unité, niveaux)
                    </Bouton>
                  )
                )}

                {message && <div style={{ fontSize: 12.5, color: 'var(--green-600)' }}>{message}</div>}
                {erreur && (
                  <div style={{ fontSize: 12.5, color: 'var(--red-600)', whiteSpace: 'pre-line', padding: '8px 10px', background: 'var(--red-50)', borderRadius: 'var(--radius-md)', border: '1px solid var(--red-200)' }}>
                    {erreur}
                  </div>
                )}
              </div>
        </Modale>
      )}

      {confirmation}
    </div>
  );
}

/** Saisie d'une calibration rattachée à un point de contrôle interne déviant. */
export function CalibrationDepuisCqi({ contexte, onFerme, onFaite, exigerTracabilite }: {
  contexte: { equipementCode: string; analyteLibelle: string; analyteCode: string; serie: string; regle: string };
  onFerme: () => void; onFaite: () => void;
  /** Repasse d'un CQI pour cause de nouveau lot réactif ou de vérification de
   *  linéarité (import PDF, 05/09/2026) : le lot réactif, le contrôle qualité
   *  utilisé pour vérifier la courbe (et son lot), et le critère de validation
   *  appliqué deviennent OBLIGATOIRES — c'est justement ce que la repasse
   *  vient documenter. Absent (déclenchement depuis un point dévié), ces
   *  quatre champs restent visibles mais facultatifs comme avant. */
  exigerTracabilite?: boolean;
}) {
  const { showToast } = useGed();
  const { run, estOccupe } = useBusy();
  const [f, setF] = useState({
    typeCalibration: 'LOT',
    // La cause est PRÉ-POSÉE mais reste modifiable : le déclenchement vient du
    // contrôle, la cause technique réelle peut être autre (réactif, maintenance).
    cause: exigerTracabilite ? 'Nouveau lot' : 'Reciblage contrôle',
    facteur: '', lotReactif: '', calibrant: '', lotCalibrant: '',
    controleQualite: '', lotControleQualite: '', critereValidationCourbe: '', remarque: '',
  });
  const [erreur, setErreur] = useState('');
  const maj = (k: keyof typeof f, v: string) => setF((x) => ({ ...x, [k]: v }));
  const incomplet = exigerTracabilite && (!f.lotReactif.trim() || !f.controleQualite.trim()
    || !f.lotControleQualite.trim() || !f.critereValidationCourbe.trim());

  return (
    <Modale
      titre={`Calibration — ${contexte.analyteLibelle} sur ${contexte.equipementCode}`}
      sousTitre={exigerTracabilite
        ? 'Calibration déclarée depuis une repasse de contrôle interne'
        : 'Tentative de résolution d’un point déviant du contrôle interne'}
      onClose={onFerme}
      largeur={640}
      pied={(
        <div style={{ display: 'flex', gap: 10, width: '100%', alignItems: 'center' }}>
          <span style={{ flex: 1 }} />
          <Bouton taille="sm" onClick={onFerme}>Annuler</Bouton>
          <Bouton variante="primaire" taille="sm" onClick={() => void run('cal', async () => {
              setErreur('');
              const r = await fetchEcriture('/api/ged/ged/calibrations', {
                method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  ...f,
                  equipementCode: contexte.equipementCode,
                  analyteLibelle: contexte.analyteLibelle,
                  analyteCode: contexte.analyteCode,
                  origine: 'cqi',
                  cqiSerie: contexte.serie,
                  cqiRegle: contexte.regle,
                }),
              });
              if (!r.ok) { setErreur((await r.json().catch(() => null))?.error?.message || 'Enregistrement refusé.'); return; }
              showToast(exigerTracabilite
                ? 'Calibration enregistrée — ajoutée à la liste des calibrations.'
                : 'Calibration enregistrée — rattachée à la série et à la règle enfreinte.');
              onFaite();
            })} disabled={estOccupe() || incomplet} title={incomplet ? 'Lot réactif, contrôle qualité, son lot et le critère de validation sont requis.' : undefined}>
            <I n="check" s={13} />{estOccupe('cal') ? 'Enregistrement…' : 'Enregistrer la calibration'}
          </Bouton>
        </div>
      )}>
      <div style={{ display: 'grid', gap: 12 }}>
        {/* Ce qui vient du POINT, en lecture seule : ces deux lignes sont la
            preuve du rattachement, elles ne se saisissent pas. */}
        <div style={{ background: 'var(--gray-25)', borderRadius: 'var(--radius-md)', padding: '10px 12px', fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-body)' }}>
          <div>Série concernée : <strong>{contexte.serie}</strong></div>
          <div>Règle enfreinte : <strong style={{ fontFamily: 'var(--font-mono)' }}>{contexte.regle || '—'}</strong></div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4 }}>
            Ces deux éléments sont recopiés depuis le point : ils disent ce qui a motivé le geste,
            et resteront lisibles même si la carte évolue.
          </div>
        </div>

        <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 14px' }}>
          <div>
            <label style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }} htmlFor="cal-type">Type</label>
            <select id="cal-type" value={f.typeCalibration} onChange={(e) => maj('typeCalibration', e.target.value)} style={inputStyle}>
              {Object.entries(CAL_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }} htmlFor="cal-cause">Cause</label>
            <select id="cal-cause" value={f.cause} onChange={(e) => maj('cause', e.target.value)} style={inputStyle}>
              {CAL_CAUSES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }} htmlFor="cal-fact">Facteur obtenu</label>
            <input id="cal-fact" value={f.facteur} onChange={(e) => maj('facteur', e.target.value)}
              placeholder="ex. 0,987" style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }} htmlFor="cal-lot">Lot de réactif{exigerTracabilite ? ' *' : ''}</label>
            <input id="cal-lot" value={f.lotReactif} onChange={(e) => maj('lotReactif', e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }} htmlFor="cal-cq">Contrôle qualité utilisé{exigerTracabilite ? ' *' : ''}</label>
            <input id="cal-cq" value={f.controleQualite} onChange={(e) => maj('controleQualite', e.target.value)}
              placeholder="ex. BD1" style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }} htmlFor="cal-lotcq">Lot du contrôle qualité{exigerTracabilite ? ' *' : ''}</label>
            <input id="cal-lotcq" value={f.lotControleQualite} onChange={(e) => maj('lotControleQualite', e.target.value)}
              style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} />
          </div>
          <div>
            <label style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }} htmlFor="cal-critere">Critère de validation de la courbe{exigerTracabilite ? ' *' : ''}</label>
            <input id="cal-critere" value={f.critereValidationCourbe} onChange={(e) => maj('critereValidationCourbe', e.target.value)}
              placeholder="ex. R² ≥ 0,995" style={inputStyle} />
          </div>
        </div>

        <div>
          <label style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }} htmlFor="cal-rem">Remarque</label>
          <textarea id="cal-rem" rows={2} value={f.remarque} onChange={(e) => maj('remarque', e.target.value)}
            placeholder="Ce qui a été constaté, et ce qu'on attend de cette calibration."
            style={{ ...inputStyle, height: 'auto', resize: 'vertical' }} />
        </div>

        {/* Le résultat n'est PAS demandé ici : on ouvre la calibration, on ne
            préjuge pas qu'elle résout. Le repassage du contrôle le dira, et
            c'est lui qui fait foi. */}
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-faint)', lineHeight: 1.5 }}>
          {exigerTracabilite
            ? 'Cette calibration est ajoutée directement à la liste des calibrations, rattachée à la repasse qui l’a motivée.'
            : (<>Cette calibration ne clôt pas le point déviant : c’est le <strong>repassage du contrôle</strong> qui
              dira si elle a résolu quoi que ce soit. La suite à donner se consigne sur le point lui-même.</>)}
        </p>

        {erreur && <div role="alert" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--red-600)' }}>{erreur}</div>}
      </div>
    </Modale>
  );
}
